import useCatalogUpdateQueue from '../hooks/useCatalogUpdateQueue';
import usePriceListBranchOverview from '../hooks/usePriceListBranchOverview';
import { BranchOffersProvider } from '../components/quickPriceList/BranchOfferRows';
import { fetchCatalogDelta, mergeCatalogDelta } from '../utils/catalogDelta';
import PriceListPendingChangesDialog from '../components/quickPriceList/PriceListPendingChangesDialog';
import usePriceListPendingChanges from '../hooks/usePriceListPendingChanges';
import { runPartialPricingBulk, bulkPricingSummary } from '../api/bulkPricingApi';
import FormatCompletionDialog from '../components/quickPriceList/FormatCompletionDialog';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QuickPriceListHeader, { QuickPriceListFilters } from '../components/quickPriceList/QuickPriceListHeader';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  applyQuickPriceCostOverride,
  createPriceListPromotions,
  endPriceListPromotion,
  getBranches,
  getComplexPriceRules,
  getPriceListConfigurations,
  previewQuickPriceCostOverride,
  recalculateQuickPriceListFormats,
  replacePriceListPromotion,
  updatePriceListFormatSettings,
  createPriceListOffers,
  editPriceListOffer,
  endPriceListOffer,
  updateQuickPriceListFormat,
  updateQuickPriceListFormatLocks,
  updateQuickPriceListFormatsBulk,
  updateQuickPriceListProductAvailability,
  getPriceListAvailabilityBranches,
  updatePriceListAvailabilityBranches,
} from '../api';
import ConfirmModal from '../components/ConfirmModal';
import EditPriceListOfferDialog from '../components/quickPriceList/EditPriceListOfferDialog';
import ProductAvailabilityDialog from '../components/quickPriceList/ProductAvailabilityDialog';
import InlineLoading from '../components/InlineLoading';
import PromotionDateRangeDialog from '../components/quickPriceList/PromotionDateRangeDialog';
import { isOfferFormat } from '../components/quickPriceList/OfferAvailabilityDialog';
import QuickPriceBulkEditor, {
  MAX_BULK_PROMOTION_COUNT,
} from '../components/quickPriceList/QuickPriceBulkEditor';
import QuickPriceCatalog from '../components/quickPriceList/QuickPriceCatalog';
import CostOverrideDialog from '../components/quickPriceList/CostOverrideDialog';
import AddStockInputDialog from '../components/quickPriceList/AddStockInputDialog';
import QuickPriceListPdfDialog from '../components/quickPriceList/QuickPriceListPdfDialog';
import {
  extractPolicyWarning,
  filterQuickPriceCatalog,
  getEditableStandardStockCostSource,
  hasStockPurchaseSource,
  getQuickPriceFormatOptions,
  getSelectableCatalogFormatIds,
  isSelectableQuickPriceFormat,
  normalizeQuickPriceCatalog,
  updateFormatInCatalog,
  updateProductAvailabilityInCatalog,
} from '../components/quickPriceList/quickPriceListModel';
import useCurrentUser from '../hooks/useCurrentUser';
import usePromotionClock from '../hooks/usePromotionClock';
import useProductEvents from '../hooks/useProductEvents';
import { packWeightLabel } from '../utils/varietyFormatIdentity';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import {
  hasPriceRuleSelection,
  normalizePriceRuleSelection,
} from '../utils/complexPriceRules';
import {
  collectCatalogPromotions,
  formatPromotionTerms,
  getPromotionState,
  isDetailPromotionTarget,
  normalizePriceListPromotion,
} from '../utils/priceListPromotions';

const resolveErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

export const getPromotionTargets = (products, formatIds) => {
  const requestedIds = new Set(Array.from(formatIds || []).map(String));
  const targets = [];
  (Array.isArray(products) ? products : []).forEach((product) => {
    (product.varieties || []).forEach((variety) => {
      (variety.formats || []).forEach((format) => {
        if (!requestedIds.has(String(format.id))) return;
        targets.push({
          id: format.id,
          label: [product.name, variety.name, [format.productFormatName || format.clientTypeLabel,
            ...(packWeightLabel(format) ? [format.metricUnit, packWeightLabel(format)] : [])].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(' · '),
          metricUnit: format.metricUnit || null,
          useScaleWeight: format.useScaleWeight === true,
          product,
          variety,
          format,
          offerFormat: variety.formats.find(isOfferFormat) || null,
          detailFormat: variety.formats.find((entry) => String(entry.productFormatCode || entry.clientType).toLowerCase() === 'detalle') || null,
        });
      });
    });
  });
  return targets;
};

export const resolveQuickFormatUpdates = (
  response,
  fallback = {},
  { operation = 'price', includeAnalysis = true, includeAvailability = false } = {}
) => {
  const source = response?.branch || response?.format?.branch || response?.data?.branch
    || response?.format || response?.data || response || {};
  const analysis = response?.analysis ?? response?.format?.analysis ?? response?.data?.analysis;
  const sellingPrice = source.sellingPrice ?? source.price ?? fallback.sellingPrice;
  const isAvailable = source.isAvailable ?? (
    source.isSoldOut === undefined ? fallback.isAvailable : !source.isSoldOut
  );
  if (operation === 'availability') {
    return {
      ...(isAvailable === undefined ? {} : { isAvailable: Boolean(isAvailable) }),
      ...(Object.hasOwn(source, 'offerAvailability') ? { offerAvailability: source.offerAvailability } : {}),
    };
  }
  return {
    ...(response?.identity || {}),
    ...(response?.completion ? { completion: response.completion, isAvailable: Boolean(source.isAvailable) } : {}),
    ...(sellingPrice === undefined ? {} : { sellingPrice }),
    ...(includeAvailability && isAvailable !== undefined
      ? { isAvailable: Boolean(isAvailable) }
      : {}),
    isConfigured: source.isConfigured ?? true,
    ...(!includeAnalysis || analysis === undefined ? {} : { analysis }),
  };
};

export const resolveQuickFormatSettingsUpdates = (
  response,
  { format = {}, payload = {}, operation } = {}
) => {
  const source = response?.format || response?.data || response || {};
  const updates = {};
  if (!isPresent(format.sellingPrice) && source.branch?.isConfigured && isPresent(source.branch.sellingPrice)) {
    updates.isConfigured = true;
    updates.sellingPrice = source.branch.sellingPrice;
    updates.isAvailable = Boolean(source.branch.isAvailable);
    if (source.completion) updates.completion = source.completion;
  }
  if (operation === 'cost') {
    const responseShared = source.shared && typeof source.shared === 'object'
      ? source.shared
      : {};
    updates.shared = {
      ...(format.shared || {}),
      manualBaseCost: Object.hasOwn(responseShared, 'manualBaseCost')
        ? responseShared.manualBaseCost
        : payload.shared?.manualBaseCost ?? null,
      ...(Object.hasOwn(responseShared, 'manualBaseCostBasis')
        ? { manualBaseCostBasis: responseShared.manualBaseCostBasis }
        : {}),
      ...(Object.hasOwn(responseShared, 'estimatedCost')
        ? { estimatedCost: responseShared.estimatedCost }
        : {}),
      ...(Object.hasOwn(responseShared, 'estimatedCostPerKg')
        ? { estimatedCostPerKg: responseShared.estimatedCostPerKg }
        : {}),
    };
  }
  if (operation === 'rule') {
    const responseBranch = source.branch && typeof source.branch === 'object'
      ? source.branch
      : {};
    const payloadBranch = payload.branch && typeof payload.branch === 'object'
      ? payload.branch
      : {};
    const responseSelection = Object.hasOwn(responseBranch, 'priceRule')
      ? responseBranch.priceRule
      : Object.hasOwn(responseBranch, 'complexPriceRuleId')
        && responseBranch.complexPriceRuleId !== null
        && responseBranch.complexPriceRuleId !== undefined
        ? { type: 'complex', complexPriceRuleId: responseBranch.complexPriceRuleId }
        : undefined;
    const requestedSelection = Object.hasOwn(payloadBranch, 'priceRule')
      ? payloadBranch.priceRule
      : undefined;
    const legacyRule = Object.hasOwn(responseBranch, 'stockPriceRule')
      ? responseBranch.stockPriceRule
      : Object.hasOwn(payloadBranch, 'stockPriceRule')
        ? payloadBranch.stockPriceRule
        : null;
    const priceRule = normalizePriceRuleSelection(
      responseSelection !== undefined ? responseSelection : requestedSelection,
      legacyRule
    );
    if (Object.hasOwn(responseBranch, 'priceRuleAssignment')) updates.priceRuleAssignment = responseBranch.priceRuleAssignment;
    updates.priceRule = priceRule;
    updates.stockPriceRule = priceRule?.type === 'simple' ? priceRule.rule : null;
  }
  if (source.costs !== undefined) updates.costs = source.costs;
  if (source.analysis !== undefined) updates.analysis = source.analysis;
  return updates;
};

const buildPolicyContextFingerprint = (product, format) => JSON.stringify([
  product?.pricingBasis || null,
  format?.sellingPrice ?? null,
  format?.isConfigured ?? false,
  format?.shared || null,
  format?.priceRule || null,
  format?.stockPriceRule || null,
  format?.costs || null,
  format?.analysis || null,
]);

const buildPolicyContextMap = (products) => {
  const contexts = new Map();
  products.forEach((product) => {
    (product.varieties || []).forEach((variety) => {
      (variety.formats || []).forEach((format) => {
        contexts.set(String(format.id), buildPolicyContextFingerprint(product, format));
      });
    });
  });
  return contexts;
};

const findCatalogFormat = (products, formatId) => {
  for (const product of products) {
    for (const variety of product.varieties || []) {
      const format = (variety.formats || []).find(
        (entry) => String(entry.id) === String(formatId)
      );
      if (format) return format;
    }
  }
  return null;
};

export const getNewlyPriceLockedFormatIds = (previousProducts, nextProducts) => {
  const previousLocks = new Map();
  (Array.isArray(previousProducts) ? previousProducts : []).forEach((product) => {
    (product.varieties || []).forEach((variety) => {
      (variety.formats || []).forEach((format) => {
        if (format?.id === null || format?.id === undefined) return;
        previousLocks.set(String(format.id), Boolean(format.isPriceLocked));
      });
    });
  });
  const newlyLockedIds = [];
  (Array.isArray(nextProducts) ? nextProducts : []).forEach((product) => {
    (product.varieties || []).forEach((variety) => {
      (variety.formats || []).forEach((format) => {
        const formatId = String(format?.id ?? '');
        if (!formatId || !format.isPriceLocked || previousLocks.get(formatId) !== false) return;
        newlyLockedIds.push(formatId);
      });
    });
  });
  return newlyLockedIds;
};

const updatePromotionsForFormat = (products, formatId, update) => {
  const currentFormat = findCatalogFormat(products, formatId);
  if (!currentFormat) return products;
  const currentPromotions = Array.isArray(currentFormat.promotions)
    ? currentFormat.promotions
    : [];
  return updateFormatInCatalog(products, formatId, {
    promotions: update(currentPromotions),
  });
};

const MAX_PRICE_LIST_SELLING_PRICE = 2147483647;
const PRICE_RULE_MODES = new Set(['markup_amount', 'markup_percent', 'fixed_price']);
const PRICE_RULE_BASES = new Set(['unit', 'kg']);
const RECALCULATION_DIRTY_FIELDS = ['cost', 'rule', 'price'];
const RECALCULATION_DIRTY_DRAFT_ERROR = (
  'Guarda o descarta los borradores visibles antes de recalcular.'
);
const RECALCULATION_REASON_ALIASES = Object.freeze({
  missing_kg_per_sale_unit: 'missing_unit_weight',
  pending_unit_weight: 'missing_unit_weight',
  missing_weight: 'missing_unit_weight',
  missing_unit_weight: 'missing_unit_weight',
  pending_basis_conversion: 'basis_conversion_required',
  missing_conversion: 'basis_conversion_required',
  basis_conversion_required: 'basis_conversion_required',
  missing_stock_price_rule: 'no_stock_price_rule',
  no_stock_price_rule: 'no_stock_price_rule',
  missing_reference_cost: 'missing_reference_cost',
  missing_stock: 'missing_reference_cost',
  missing_total_kg: 'missing_reference_cost',
});

const isPresent = (value) => (
  value !== null && value !== undefined && String(value).trim() !== ''
);

const parseFiniteNumber = (value) => {
  if (!isPresent(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePricingBasis = (value) => (
  String(value || '').trim().toLowerCase() === 'kg' ? 'kg' : 'unit'
);

const normalizeRecalculationRule = (rule, { defaultBasis = 'unit' } = {}) => {
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return null;
  const mode = String(rule.mode || '').trim().toLowerCase();
  const value = parseFiniteNumber(rule.value);
  const basis = String(rule.basis || defaultBasis || 'unit').trim().toLowerCase();
  if (!PRICE_RULE_MODES.has(mode) || value === null || value < 0
    || !PRICE_RULE_BASES.has(basis)) return null;
  return {
    mode,
    value: mode === 'markup_percent' ? value : Math.round(value),
    basis,
  };
};

const normalizeRecalculationReason = (reason) => (
  Object.hasOwn(RECALCULATION_REASON_ALIASES, reason)
    ? RECALCULATION_REASON_ALIASES[reason]
    : 'invalid_stock_price_rule'
);

const getKgPerSaleUnit = (format = {}) => {
  const metricUnit = String(format.metricUnit || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const metricUnitIsKg = metricUnit.includes('kg')
    || metricUnit.includes('kilo')
    || metricUnit.includes('granel');
  if (Boolean(format.useScaleWeight) || metricUnitIsKg) return 1;
  const unitWeightKg = parseFiniteNumber(format.unitWeightKg);
  return unitWeightKg !== null && unitWeightKg > 0 ? unitWeightKg : null;
};

const hasRecalculationBasisIssue = ({ product, format, rule }) => {
  const targetBasis = normalizePricingBasis(
    product?.pricingBasis ?? format.analysis?.pricingBasis
  );
  const kgPerSaleUnit = getKgPerSaleUnit(format);
  const ruleNeedsConversion = rule.basis !== targetBasis;
  const analyzedReferenceCost = format.analysis?.referenceCost;
  const analyzedBasisValue = parseFiniteNumber(analyzedReferenceCost?.basisValue);
  const hasActiveStockReferenceCost = (
    String(analyzedReferenceCost?.source || '').trim().toLowerCase() === 'stock_list'
    && analyzedBasisValue !== null
    && analyzedBasisValue >= 0
  );
  const estimatedCost = format.shared?.estimatedCost ?? format.estimatedCost;
  const estimatedCostPerKg = format.shared?.estimatedCostPerKg ?? format.estimatedCostPerKg;
  const estimatedCostNeedsConversion = !hasActiveStockReferenceCost
    && targetBasis === 'unit'
    && parseFiniteNumber(estimatedCostPerKg) !== null
    && parseFiniteNumber(estimatedCost) === null;
  const requiresKgFactor = targetBasis === 'kg'
    || ruleNeedsConversion
    || estimatedCostNeedsConversion;
  const missingUnitWeight = requiresKgFactor && kgPerSaleUnit === null;
  return missingUnitWeight || ruleNeedsConversion || estimatedCostNeedsConversion;
};

const buildRecalculationCandidate = (product, format) => {
  if (!format?.isConfigured || format.productFormatActive === false || isOfferFormat(format)) return null;
  const selection = normalizePriceRuleSelection(format.priceRule, format.stockPriceRule);
  if (!selection) return null;

  const analysis = format.analysis || {};
  if (analysis.priceRule?.error) return null;
  const resolvedRule = analysis.priceRule?.resolvedRule
    || (selection.type === 'simple' ? selection.rule : null);
  const rule = normalizeRecalculationRule(resolvedRule);
  if (!rule || hasRecalculationBasisIssue({ product, format, rule })) return null;

  const pendingReasons = Array.isArray(analysis.pendingReasons)
    ? analysis.pendingReasons.map(normalizeRecalculationReason)
    : [];
  if (pendingReasons.some((reason) => (
    reason === 'missing_unit_weight' || reason === 'basis_conversion_required'
  ))) return null;

  const isFixedPrice = rule.mode === 'fixed_price';
  const referenceCost = analysis.referenceCost?.basisValue;
  if (!isFixedPrice && (!Number.isFinite(referenceCost) || referenceCost < 0)) return null;
  if (analysis.stockPriceRule?.error) {
    pendingReasons.push(normalizeRecalculationReason(analysis.stockPriceRule.error));
  }
  if (pendingReasons.some((reason) => (
    !isFixedPrice || reason !== 'missing_reference_cost'
  ))) return null;

  const sellingPrice = analysis.stockPriceRule?.sellingPrice;
  if (!Number.isSafeInteger(sellingPrice) || sellingPrice < 0
    || sellingPrice > MAX_PRICE_LIST_SELLING_PRICE) return null;
  const currentSellingPrice = parseFiniteNumber(format.sellingPrice);
  if (Number.isSafeInteger(currentSellingPrice) && currentSellingPrice === sellingPrice) return null;
  return { sellingPrice };
};

const hasDirtyRecalculationDraft = (dirtyFields, formatIds) => (
  Array.from(formatIds || []).some((formatId) => (
    RECALCULATION_DIRTY_FIELDS.some((field) => (
      dirtyFields?.[`${String(formatId)}:${field}`]
    ))
  ))
);

export const getRecalculationDraftError = (dirtyFields, formatIds) => (
  hasDirtyRecalculationDraft(dirtyFields, formatIds)
    ? RECALCULATION_DIRTY_DRAFT_ERROR
    : ''
);

export const deriveRecalculationReadyFormats = (products) => {
  const ready = {};
  (Array.isArray(products) ? products : []).forEach((product) => {
    (product.varieties || []).forEach((variety) => {
      (variety.formats || []).forEach((format) => {
        const candidate = buildRecalculationCandidate(product, format);
        if (!candidate) return;
        ready[String(format.id)] = true;
      });
    });
  });
  return ready;
};

export const getCatalogFormatIds = (products) => (
  (Array.isArray(products) ? products : []).flatMap((product) => (
    (product.varieties || []).flatMap((variety) => (
      (variety.formats || []).flatMap((format) => (
        format?.productFormatActive === false
          || format?.id === null
          || format?.id === undefined
          ? []
          : [format.id]
      ))
    ))
  ))
);

export const resolveBulkFormatUpdates = (
  responseFormat,
  currentFormat = {},
  { includePrice = true, includeRule = true } = {}
) => {
  const source = responseFormat?.format || responseFormat?.data || responseFormat || {};
  const branch = source.branch && typeof source.branch === 'object' ? source.branch : {};
  const updates = {};
  if (includePrice && Object.hasOwn(branch, 'sellingPrice')) {
    updates.sellingPrice = branch.sellingPrice;
  }
  if (includePrice && Object.hasOwn(branch, 'isConfigured')) {
    updates.isConfigured = Boolean(branch.isConfigured);
  }
  if (includePrice && !currentFormat.isConfigured && Object.hasOwn(branch, 'isAvailable')) {
    updates.isAvailable = Boolean(branch.isAvailable);
  }
  if (includeRule && (
    Object.hasOwn(branch, 'priceRule')
    || Object.hasOwn(branch, 'stockPriceRule')
    || Object.hasOwn(branch, 'complexPriceRuleId')
  )) {
    const explicitSelection = Object.hasOwn(branch, 'priceRule')
      ? branch.priceRule
      : branch.complexPriceRuleId !== null && branch.complexPriceRuleId !== undefined
        ? { type: 'complex', complexPriceRuleId: branch.complexPriceRuleId }
        : undefined;
    const priceRule = normalizePriceRuleSelection(explicitSelection, branch.stockPriceRule);
    if (Object.hasOwn(branch, 'priceRuleAssignment')) updates.priceRuleAssignment = branch.priceRuleAssignment;
    updates.priceRule = priceRule;
    updates.stockPriceRule = priceRule?.type === 'simple' ? priceRule.rule : null;
  }
  if (Object.hasOwn(source, 'costs')) updates.costs = source.costs;
  if (Object.hasOwn(source, 'analysis')) updates.analysis = source.analysis;
  return updates;
};

export const resolvePriceLockUpdates = (response, requestedFormatIds, requestedIsLocked) => {
  const responseFormats = Array.isArray(response?.formats)
    ? response.formats
    : Array.isArray(response?.data?.formats) ? response.data.formats : [];
  const locksById = new Map(responseFormats.flatMap((entry) => {
    const formatId = entry?.varietyPriceId ?? entry?.format?.varietyPriceId ?? entry?.id;
    const isPriceLocked = entry?.branch?.isPriceLocked
      ?? entry?.format?.branch?.isPriceLocked
      ?? entry?.isPriceLocked;
    return formatId === null || formatId === undefined || isPriceLocked === undefined
      ? []
      : [[String(formatId), Boolean(isPriceLocked)]];
  }));
  const responseIds = Array.isArray(response?.formatIds)
    ? response.formatIds
    : Array.isArray(response?.data?.formatIds) ? response.data.formatIds : [];
  const targetIds = response?.operationId || responseIds.length ? responseIds : requestedFormatIds;
  const fallbackLocked = response?.isLocked ?? response?.data?.isLocked ?? requestedIsLocked;
  return Array.from(new Set((targetIds || []).map(String))).map((formatId) => ({
    formatId,
    isPriceLocked: locksById.has(formatId)
      ? locksById.get(formatId)
      : Boolean(fallbackLocked),
  }));
};

const QuickPriceListPage = () => {
  const stickyToolbarRef = useRef(null);
  const { branchId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, status } = useCurrentUser();
  const canRead = hasPermission(user, 'price.read');
  const canUpdate = hasPermission(user, 'price.update');
  const canUpdateProduct = hasPermission(user, 'product.update');
  const [completionTarget, setCompletionTarget] = useState(null);
  const [pendingTarget, setPendingTarget] = useState(null);
  const canCorrectStockCosts = hasPermission(user, 'stock.update');
  const canCreateStock = hasPermission(user, 'stock.read') && hasPermission(user, 'stock.create');
  const canReadProviders = hasPermission(user, 'provider.read');
  const targetFormatId = searchParams.get('varietyPriceId') || '';

  const [catalog, setCatalog] = useState([]);
  const branchOverview = usePriceListBranchOverview({ branchId, companyId: user?.selectedCompanyId, enabled: canRead, refreshKey: catalog });
  const canUpdateRegularPricing = canUpdate && branchOverview.data?.isMainBranch === true;
  useEffect(() => {
    if (searchParams.get('main') !== '1' || !branchOverview.data?.mainBranch) return;
    const params = new URLSearchParams(searchParams);
    params.delete('main');
    const queryString = params.toString();
    navigate(`/branches/${branchOverview.data.mainBranch.id}/price-list${queryString ? `?${queryString}` : ''}`, { replace: true });
  }, [branchOverview.data?.mainBranch, searchParams, navigate]);
  const [promotionClock, setPromotionClock] = useState(null);
  const [complexPriceRules, setComplexPriceRules] = useState([]);
  const [catalogBranchId, setCatalogBranchId] = useState('');
  const [catalogGeneration, setCatalogGeneration] = useState(0);
  const [loading, setLoading] = useState(true);
  const catalogLoading = loading || Boolean(canRead && branchId && catalogBranchId !== String(branchId));
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [pageError, setPageError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [formatCode, setFormatCode] = useState('');
  const canUpdatePricing = canUpdateRegularPricing || (canUpdate && isOfferFormat({ productFormatCode: formatCode }));
  const [categoryId, setCategoryId] = useState('');
  const [savingPrices, setSavingPrices] = useState({});
  const [savingAvailability, setSavingAvailability] = useState({});
  const [savingProducts, setSavingProducts] = useState({});
  const [savingSettings, setSavingSettings] = useState({});
  const [savingLocks, setSavingLocks] = useState({});
  const [recalculatingFormats, setRecalculatingFormats] = useState({});
  const [dirtyFormatFields, setDirtyFormatFields] = useState({});
  const [savingPromotions, setSavingPromotions] = useState({});
  const [promotionErrors, setPromotionErrors] = useState({});
  const [promotionDialog, setPromotionDialog] = useState(null);
  const [offerAction, setOfferAction] = useState(null);
  const [offerActionError, setOfferActionError] = useState('');
  const [promotionDialogError, setPromotionDialogError] = useState('');
  const [promotionBranches, setPromotionBranches] = useState([]);
  const [promotionBranchesLoading, setPromotionBranchesLoading] = useState(false);
  const [promotionBranchesError, setPromotionBranchesError] = useState('');
  const creatingPromotion = Boolean(promotionDialog && !promotionDialog.promotion);
  const canReadBranches = hasPermission(user, 'branch.read');
  const promotionBranchOptions = useMemo(() => {
    const options = promotionBranches.filter((branch) => !branch.isDeleted);
    return options.some((branch) => String(branch.id) === String(branchId))
      ? options
      : [{ id: branchId, branchName: `Sucursal ${branchId} (actual)` }, ...options];
  }, [branchId, promotionBranches]);
  useEffect(() => {
    if (!canReadBranches) return undefined;
    let active = true;
    setPromotionBranches([]);
    setPromotionBranchesLoading(true);
    setPromotionBranchesError('');
    const loadBranches = async () => {
      try {
        const response = await getBranches({ pageSize: 0 });
        if (active) setPromotionBranches(normalizePaginatedResponse(response).data || []);
      } catch {
        if (active) setPromotionBranchesError('No se pudieron cargar las otras sucursales. Cierra y vuelve a abrir para reintentar.');
      } finally {
        if (active) setPromotionBranchesLoading(false);
      }
    };
    void loadBranches();
    return () => { active = false; };
  }, [creatingPromotion, canReadBranches, user?.selectedCompanyId]);
  const [promotionConfirmation, setPromotionConfirmation] = useState(null);
  const [errorsByFormat, setErrorsByFormat] = useState({});
  const [priceResetSignals, setPriceResetSignals] = useState({});
  const [costResetSignals, setCostResetSignals] = useState({});
  const [ruleResetSignals, setRuleResetSignals] = useState({});
  const [selection, setSelectedFormatIds] = useState(() => new Set());
  const [savingBulk, setSavingBulk] = useState(false);
  const [bulkRefresh, setBulkRefresh] = useState(0);
  const pendingChanges = usePriceListPendingChanges({ branchId, userId: user?.id, enabled: canRead, refreshKey: bulkRefresh });
  useEffect(() => { setPendingTarget(null); }, [branchId, categoryId, formatCode, query]);
  const [recalculationConfirmation, setRecalculationConfirmation] = useState(null);
  const [recalculationResult, setRecalculationResult] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [productConfirmation, setProductConfirmation] = useState(null);
  const [availabilityScope, setAvailabilityScope] = useState(null);
  const [availabilityScopeRetry, setAvailabilityScopeRetry] = useState(0);
  const productAvailabilityOperationRef = useRef(null);
  useEffect(() => {
    if (!canUpdate || !branchId) return undefined;
    let active = true;
    const requestedBranchId = String(branchId);
    setAvailabilityScope({ branchId: requestedBranchId, loading: true });
    getPriceListAvailabilityBranches(branchId).then((scope) => {
      if (active) setAvailabilityScope({ ...scope, branchId: requestedBranchId, loading: false });
    }).catch(() => {
      if (active) setAvailabilityScope({ branchId: requestedBranchId, loading: false,
        error: 'No se pudieron cargar las sucursales. Reintenta antes de confirmar.' });
    });
    return () => { active = false; };
  }, [branchId, canUpdate, availabilityScopeRetry, user?.selectedCompanyId]);
  const [costOverrideReview, setCostOverrideReview] = useState(null);
  const [stockInputTarget, setStockInputTarget] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const eventRefreshTimerRef = useRef(null);
  const initializedTargetRef = useRef('');
  const bulkSelectionVersionRef = useRef(0);
  const recalculationScopeVersionRef = useRef(0);
  const catalogRequestIdRef = useRef(0);
  const catalogRef = useRef(catalog);
  const catalogBranchIdRef = useRef(catalogBranchId);
  const hasCatalogSnapshotRef = useRef(false);
  const priceDraftVersionsRef = useRef({});
  const policyContextVersionsRef = useRef({});
  const settingsMutationVersionsRef = useRef({});
  const dirtyFormatFieldsRef = useRef({});
  const formatRecalculationOperationsRef = useRef(new Map());
  const pendingCostCommitRef = useRef(null);
  const promotionMutationsRef = useRef(new Map());
  const bulkMutationRef = useRef({ formatIds: new Set(), price: false, rule: false });
  const priceLockMutationRef = useRef(null);
  const visibleCatalogRef = useRef([]);
  const pendingPriceFocusRef = useRef(null);
  const activeBranchIdRef = useRef(String(branchId || ''));
  const branchGenerationRef = useRef(0);
  activeBranchIdRef.current = String(branchId || '');
  const bumpPolicyContextVersion = useCallback((formatId) => {
    const key = String(formatId);
    const next = (policyContextVersionsRef.current[key] || 0) + 1;
    policyContextVersionsRef.current[key] = next;
    return next;
  }, []);
  const invalidatePolicyContext = bumpPolicyContextVersion;

  useEffect(() => {
    pendingCostCommitRef.current?.resolve(false);
    pendingCostCommitRef.current = null;
    branchGenerationRef.current += 1;
    catalogRequestIdRef.current += 1;
    if (eventRefreshTimerRef.current) {
      window.clearTimeout(eventRefreshTimerRef.current);
      eventRefreshTimerRef.current = null;
    }
    // Keep the last view visible while loading, but detach it from all mutations.
    // The new snapshot remounts its editors so drafts cannot cross branches.
    if (!canRead) {
      setCatalog([]);
      setCatalogBranchId('');
      setFormatCode('');
      setCategoryId('');
    }
    setPromotionClock(null);
    catalogRef.current = [];
    catalogBranchIdRef.current = '';
    hasCatalogSnapshotRef.current = false;
    setLoadError('');
    setRefreshError('');
    setPageError('');
    setNotice('');
    initializedTargetRef.current = '';
    setSavingPrices({});
    setSavingAvailability({});
    setSavingProducts({});
    setSavingSettings({});
    setSavingLocks({});
    setRecalculatingFormats({});
    setDirtyFormatFields({});
    setSavingPromotions({});
    setPromotionErrors({});
    setPromotionDialog(null);
    setOfferAction(null);
    setOfferActionError('');
    setPromotionDialogError('');
    setPromotionConfirmation(null);
    promotionMutationsRef.current = new Map();
    setErrorsByFormat({});
    setPriceResetSignals({});
    setCostResetSignals({});
    setRuleResetSignals({});
    setSelectedFormatIds(new Set());
    setSavingBulk(false);
    setRecalculating(false);
    setRecalculationConfirmation(null);
    setRecalculationResult(null);
    priceDraftVersionsRef.current = {};
    policyContextVersionsRef.current = {};
    settingsMutationVersionsRef.current = {};
    dirtyFormatFieldsRef.current = {};
    formatRecalculationOperationsRef.current = new Map();
    pendingPriceFocusRef.current = null;
    bulkMutationRef.current = { formatIds: new Set(), price: false, rule: false };
    priceLockMutationRef.current = null;
    setProductConfirmation(null);
    productAvailabilityOperationRef.current = null;
    setCostOverrideReview(null);
    setStockInputTarget(null);
    setPdfOpen(false);
    setLoading(Boolean(branchId && canRead));
    setRefreshing(false);
  }, [branchId, canRead]);

  const loadCatalog = useCallback(async ({ refresh = false, silent = false, event, propagateError = false } = {}) => {
    const requestBranchId = String(branchId || '');
    if (!requestBranchId || !canRead || activeBranchIdRef.current !== requestBranchId) return;
    const requestId = catalogRequestIdRef.current + 1;
    catalogRequestIdRef.current = requestId;
    const canPreserveCatalog = hasCatalogSnapshotRef.current
      && catalogBranchIdRef.current === requestBranchId;
    if (refresh) setRefreshing(true);
    else if (!silent) setLoading(true);
    if ((!refresh && !silent) || !canPreserveCatalog) setLoadError('');
    if (refresh || silent) setRefreshError('');
    try {
      const [response, complexRulesResult] = await Promise.all([
        fetchCatalogDelta((params) => Object.keys(params).length ? getPriceListConfigurations(branchId, params) : getPriceListConfigurations(branchId), canPreserveCatalog ? event : undefined, catalogRef.current),
        Promise.resolve(getComplexPriceRules())
          .then((rules) => ({ rules }))
          .catch(() => ({ rules: null })),
      ]);
      if (requestId !== catalogRequestIdRef.current
        || activeBranchIdRef.current !== requestBranchId) return;
      const nextCatalog = mergeCatalogDelta(catalogRef.current, normalizeQuickPriceCatalog(response), response?.deltaProductIds);
      const previousCatalog = catalogRef.current;
      const previousContexts = buildPolicyContextMap(previousCatalog);
      const nextContexts = buildPolicyContextMap(nextCatalog);
      new Set([...previousContexts.keys(), ...nextContexts.keys()]).forEach((formatId) => {
        if (previousContexts.get(formatId) !== nextContexts.get(formatId)) {
          bumpPolicyContextVersion(formatId);
        }
      });
      const newlyLockedFormatIds = getNewlyPriceLockedFormatIds(previousCatalog, nextCatalog);
      if (newlyLockedFormatIds.length) {
        const nextDirtyFields = { ...dirtyFormatFieldsRef.current };
        newlyLockedFormatIds.forEach((formatId) => {
          delete nextDirtyFields[`${formatId}:price`];
          delete nextDirtyFields[`${formatId}:rule`];
          priceDraftVersionsRef.current[formatId] = (
            priceDraftVersionsRef.current[formatId] || 0
          ) + 1;
        });
        dirtyFormatFieldsRef.current = nextDirtyFields;
        setDirtyFormatFields(nextDirtyFields);
        setPriceResetSignals((current) => {
          const next = { ...current };
          newlyLockedFormatIds.forEach((formatId) => {
            next[formatId] = (next[formatId] || 0) + 1;
          });
          return next;
        });
        setRuleResetSignals((current) => {
          const next = { ...current };
          newlyLockedFormatIds.forEach((formatId) => {
            next[formatId] = (next[formatId] || 0) + 1;
          });
          return next;
        });
      }
      catalogRef.current = nextCatalog;
      setCatalog(nextCatalog);
      setPromotionClock(response?.promotionClock || null);
      if (Array.isArray(complexRulesResult.rules)) {
        setComplexPriceRules(complexRulesResult.rules);
      }
      setLoadError('');
      setRefreshError('');
      setCatalogBranchId(requestBranchId);
      setCatalogGeneration(branchGenerationRef.current);
      catalogBranchIdRef.current = requestBranchId;
      hasCatalogSnapshotRef.current = true;
      return nextCatalog;
    } catch (error) {
      if (requestId !== catalogRequestIdRef.current
        || activeBranchIdRef.current !== requestBranchId) return;
      if ((refresh || silent) && canPreserveCatalog) {
        setRefreshError(resolveErrorMessage(error, 'No se pudo actualizar la lista de precios.'));
        if (propagateError) throw error;
      } else {
        catalogRef.current = [];
        setCatalog([]);
        setCatalogBranchId(requestBranchId);
        catalogBranchIdRef.current = requestBranchId;
        hasCatalogSnapshotRef.current = false;
        setLoadError(resolveErrorMessage(error, 'No se pudo cargar la lista de precios.'));
      }
    } finally {
      if (requestId === catalogRequestIdRef.current
        && activeBranchIdRef.current === requestBranchId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [branchId, bumpPolicyContextVersion, canRead]);

  const handleAddStockInput = ({ product, variety, format }) => {
    if (!canCreateStock || stockInputTarget || costOverrideReview
      || catalogBranchIdRef.current !== String(branchId)) return;
    const currentProduct = catalogRef.current.find((entry) => String(entry.id) === String(product.id));
    const currentVariety = currentProduct?.varieties.find((entry) => String(entry.id) === String(variety.id));
    const currentFormat = currentVariety?.formats.find((entry) => String(entry.id) === String(format.id));
    if (!currentFormat || currentVariety.formats.some((entry) => hasStockPurchaseSource(entry.costs))) return;
    setStockInputTarget({ product: currentProduct, variety: currentVariety, format: currentFormat,
      branchId: String(branchId), generation: branchGenerationRef.current });
  };

  const handleStockInputSaved = async () => {
    const target = stockInputTarget;
    if (!target || activeBranchIdRef.current !== target.branchId
      || branchGenerationRef.current !== target.generation) return null;
    if (eventRefreshTimerRef.current) {
      window.clearTimeout(eventRefreshTimerRef.current);
      eventRefreshTimerRef.current = null;
    }
    const refreshed = await loadCatalog({ silent: true, event: { priceIds: [target.format.id] } });
    if (activeBranchIdRef.current !== target.branchId
      || branchGenerationRef.current !== target.generation) return null;
    return refreshed ? findCatalogFormat(refreshed, target.format.id) : null;
  };

  useEffect(() => {
    if (status === 'loading' || status === 'idle') return;
    if (!canRead) {
      setLoading(false);
      return;
    }
    void loadCatalog();
  }, [canRead, loadCatalog, status]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const scheduleEventRefresh = useCatalogUpdateQueue(branchId, (event) => loadCatalog({ silent: true, event, propagateError: true }));

  useProductEvents(branchId, scheduleEventRefresh);

  const catalogPromotions = useMemo(() => [
    ...collectCatalogPromotions(catalog),
    ...(branchOverview.data?.offers || []),
    ...catalog.flatMap((product) => product.varieties.flatMap((variety) => variety.formats
      .filter((format) => isOfferFormat(format) && format.offerAvailability && !format.offerAvailability.endedAt)
      .map((format) => ({ ...format.offerAvailability,
        startsAt: format.offerAvailability.startsAt || format.offerAvailability.enabledAt })))),
  ], [catalog, branchOverview.data?.offers]);
  const promotionNow = usePromotionClock(
    catalogPromotions,
    scheduleEventRefresh,
    promotionClock
  );

  // A row recalculation disables every editor in that row. Guarded cost inputs
  // may also finish their own focus restoration during that render, so restore
  // the requested price focus from the parent after child effects have settled.
  // Keep the request through the silent catalog refresh so its second render
  // cannot leave focus on a neighboring row action.
  useEffect(() => {
    const pending = pendingPriceFocusRef.current;
    if (!pending
      || pending.branchId !== activeBranchIdRef.current
      || pending.generation !== branchGenerationRef.current
      || recalculatingFormats[pending.formatId]) return;
    const priceInput = document.getElementById(`quick-price-${pending.formatId}`);
    if (priceInput && !priceInput.disabled) priceInput.focus();
  }, [catalog, recalculatingFormats]);

  useEffect(() => () => {
    if (eventRefreshTimerRef.current) window.clearTimeout(eventRefreshTimerRef.current);
    pendingCostCommitRef.current?.resolve(false);
    pendingCostCommitRef.current = null;
  }, []);

  const categoryOptions = useMemo(() => {
    const categories = new Map();
    catalog.forEach((product) => {
      const id = product.category?.id ?? 'uncategorized';
      categories.set(String(id), product.category?.name || (
        id === 'uncategorized' ? 'Sin categoría' : `Categoría ${id}`
      ));
    });
    return Array.from(categories, ([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog]);

  const formatOptions = useMemo(
    () => getQuickPriceFormatOptions(catalog),
    [catalog]
  );

  useEffect(() => {
    if (formatCode && !formatOptions.some((option) => option.value === formatCode)) {
      setFormatCode('');
    }
  }, [formatCode, formatOptions]);

  useEffect(() => {
    if (categoryId && !categoryOptions.some((option) => option.id === categoryId)) {
      setCategoryId('');
    }
  }, [categoryId, categoryOptions]);

  // A link from the product catalog already identifies a specific category and format.
  useEffect(() => {
    if (!targetFormatId) {
      initializedTargetRef.current = '';
      return;
    }
    if (catalogBranchId !== String(branchId)) return;
    const targetKey = `${branchId}:${targetFormatId}`;
    if (initializedTargetRef.current === targetKey) return;
    for (const product of catalog) {
      const format = product.varieties.flatMap((variety) => variety.formats).find(
        (entry) => String(entry.id) === targetFormatId && entry.productFormatActive !== false
      );
      if (!format) continue;
      initializedTargetRef.current = targetKey;
      setCategoryId(String(product.category?.id ?? 'uncategorized'));
      setFormatCode(format.productFormatCode || format.clientType || '');
      break;
    }
  }, [branchId, catalog, catalogBranchId, targetFormatId]);

  const hasRequiredFilters = categoryOptions.some((option) => option.id === categoryId)
    && formatOptions.some((option) => option.value === formatCode);
  const filteredCatalog = useMemo(() => hasRequiredFilters ? filterQuickPriceCatalog(catalog, {
    query,
    formatCode,
    categoryId,
    targetFormatId,
  }) : [], [catalog, categoryId, formatCode, hasRequiredFilters, query, targetFormatId]);
  visibleCatalogRef.current = filteredCatalog;

  useEffect(() => {
    if (!hasRequiredFilters) {
      bulkSelectionVersionRef.current += 1;
      setSelectedFormatIds((current) => current.size ? new Set() : current);
    }
  }, [hasRequiredFilters]);

  const visibleFormatIds = useMemo(
    () => getCatalogFormatIds(filteredCatalog),
    [filteredCatalog]
  );
  const selectableVisibleFormatIds = useMemo(
    () => canUpdatePricing ? getSelectableCatalogFormatIds(filteredCatalog) : getCatalogFormatIds(filteredCatalog),
    [filteredCatalog, canUpdatePricing]
  );
  const selectedFormatIds = useMemo(() => new Set(
    selectableVisibleFormatIds.map(String).filter((id) => selection.has(id))
  ), [selectableVisibleFormatIds, selection]);
  const lockedVisibleFormatIds = useMemo(() => {
    const selectable = new Set(selectableVisibleFormatIds.map(String));
    return visibleFormatIds.filter((formatId) => !selectable.has(String(formatId)));
  }, [selectableVisibleFormatIds, visibleFormatIds]);
  const visibleFormatSignature = [...visibleFormatIds].map(String).sort().join(',');
  const allVisibleSelected = selectableVisibleFormatIds.length > 0
    && selectableVisibleFormatIds.every(
    (formatId) => selectedFormatIds.has(String(formatId))
  );
  const selectedVisibleCount = visibleFormatIds.filter(
    (formatId) => selectedFormatIds.has(String(formatId))
  ).length;
  const selectedLockedFormatIds = lockedVisibleFormatIds.filter(
    (formatId) => selectedFormatIds.has(String(formatId))
  );
  const selectedUnlockedFormatIds = selectableVisibleFormatIds.filter(
    (formatId) => selectedFormatIds.has(String(formatId))
  );
  const selectedPricingBases = useMemo(() => {
    const bases = new Set();
    catalog.forEach((product) => {
      const hasSelectedFormat = (product.varieties || []).some((variety) => (
        (variety.formats || []).some((format) => selectedFormatIds.has(String(format.id)))
      ));
      if (hasSelectedFormat) bases.add(product.pricingBasis === 'kg' ? 'kg' : 'unit');
    });
    return Array.from(bases);
  }, [catalog, selectedFormatIds]);
  const selectedUnconfiguredCount = useMemo(() => {
    let count = 0;
    catalog.forEach((product) => {
      (product.varieties || []).forEach((variety) => {
        (variety.formats || []).forEach((format) => {
          if (selectedFormatIds.has(String(format.id)) && !format.isConfigured) count += 1;
        });
      });
    });
    return count;
  }, [catalog, selectedFormatIds]);
  const selectedMutationsSaving = Array.from(selectedFormatIds).some((formatId) => (
    savingPrices[formatId]
    || savingSettings[`${formatId}:cost`]
    || savingSettings[`${formatId}:rule`]
    || savingLocks[formatId]
    || recalculatingFormats[formatId]
  ));
  const anyLockSaving = Object.values(savingLocks).some(Boolean);
  const anyPromotionSaving = Object.values(savingPromotions).some(Boolean);
  const anyFormatRecalculating = Object.values(recalculatingFormats).some(Boolean);
  const recalculationReadyFormats = useMemo(
    () => deriveRecalculationReadyFormats(catalog),
    [catalog]
  );
  const actionableRecalculationFormats = useMemo(() => {
    const next = {};
    Object.entries(recalculationReadyFormats).forEach(([formatId, ready]) => {
      if (!ready
        || dirtyFormatFields[`${formatId}:cost`]
        || dirtyFormatFields[`${formatId}:rule`]
        || dirtyFormatFields[`${formatId}:price`]) return;
      next[formatId] = true;
    });
    return next;
  }, [dirtyFormatFields, recalculationReadyFormats]);
  const visibleRowsHaveDirtyDrafts = hasDirtyRecalculationDraft(
    dirtyFormatFields,
    visibleFormatIds
  );
  const anyMutationSaving = [
    savingPrices,
    savingSettings,
    savingAvailability,
    savingProducts,
    savingPromotions,
    savingLocks,
    recalculatingFormats,
  ]
    .some((entries) => Object.values(entries).some(Boolean));
  const visibleRowsHaveErrors = visibleFormatIds.some((id) => Boolean(errorsByFormat[id]));
  const pendingRecalculationCount = visibleFormatIds.filter((id) => recalculationReadyFormats[id]).length;
  const recalculationDisabled = !canUpdateRegularPricing || !pendingRecalculationCount || refreshing || savingBulk || anyMutationSaving
    || Boolean(costOverrideReview)
    || Boolean(stockInputTarget)
    || visibleRowsHaveErrors || visibleRowsHaveDirtyDrafts
    || !hasRequiredFilters || !visibleFormatIds.length
    || visibleFormatIds.length > 1000;

  useEffect(() => {
    recalculationScopeVersionRef.current += 1;
    setRecalculationConfirmation(null);
    setRecalculationResult(null);
    setProductConfirmation(null);
    setPromotionDialog(null);
  }, [branchId, categoryId, formatCode, query, targetFormatId, visibleFormatSignature]);

  useEffect(() => {
    const availableIds = new Set(
      selectableVisibleFormatIds.map((formatId) => String(formatId))
    );
    setSelectedFormatIds((current) => {
      const next = new Set(Array.from(current).filter((formatId) => availableIds.has(formatId)));
      if (next.size === current.size
        && Array.from(next).every((formatId) => current.has(formatId))) return current;
      bulkSelectionVersionRef.current += 1;
      return next;
    });
  }, [selectableVisibleFormatIds]);

  const handleToggleFormatSelection = useCallback((formatId, checked) => {
    const key = String(formatId);
    const currentFormat = findCatalogFormat(catalogRef.current, key);
    if (checked && (canUpdatePricing && !isSelectableQuickPriceFormat(currentFormat)
      || !findCatalogFormat(visibleCatalogRef.current, key))) return;
    bulkSelectionVersionRef.current += 1;
    setSelectedFormatIds((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, [canUpdatePricing]);

  const handleSetFormatsSelected = useCallback((formatIds, checked) => {
    const selectableIds = new Set(
      (canUpdatePricing ? getSelectableCatalogFormatIds(visibleCatalogRef.current) : getCatalogFormatIds(visibleCatalogRef.current)).map((formatId) => String(formatId))
    );
    bulkSelectionVersionRef.current += 1;
    setSelectedFormatIds((current) => {
      const next = new Set(current);
      (formatIds || []).forEach((formatId) => {
        const key = String(formatId);
        if (checked && selectableIds.has(key)) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  }, [canUpdatePricing]);

  const handleClearFormatSelection = useCallback(() => {
    bulkSelectionVersionRef.current += 1;
    setSelectedFormatIds(new Set());
  }, []);

  const clearFormatError = (formatId) => setErrorsByFormat((current) => ({
    ...current,
    [formatId]: '',
  }));

  const applyFormatResponse = useCallback((formatId, response, fallback, options) => {
    const next = updateFormatInCatalog(
      catalogRef.current,
      formatId,
      resolveQuickFormatUpdates(response, fallback, options)
    );
    catalogRef.current = next;
    setCatalog(next);
  }, []);

  const performFormatUpdate = useCallback(async ({
    format,
    payload,
    operation = 'price',
    draftVersion,
    policyContextVersion,
  }) => {
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    const isCurrentRequest = () => activeBranchIdRef.current === requestBranchId
      && branchGenerationRef.current === requestGeneration;
    if (!canUpdate
      || !requestBranchId
      || activeBranchIdRef.current !== requestBranchId
      || catalogBranchIdRef.current !== requestBranchId
      || (bulkMutationRef.current.formatIds.size > 0
        && bulkMutationRef.current.formatIds.has(String(format.id)))
      || priceLockMutationRef.current?.formatIds.has(String(format.id))
      || formatRecalculationOperationsRef.current.has(String(format.id))) return false;
    const latestFormat = findCatalogFormat(catalogRef.current, format.id) || format;
    if (isOfferFormat(latestFormat)) return false;
    if (operation === 'price' && latestFormat.isPriceLocked) {
      setErrorsByFormat((current) => ({
        ...current,
        [format.id]: 'Desbloquea este formato para cambiar el precio final.',
      }));
      return false;
    }
    const setSaving = operation === 'availability' ? setSavingAvailability : setSavingPrices;
    setSaving((current) => ({ ...current, [format.id]: true }));
    clearFormatError(format.id);
    setPageError('');
    try {
      // Below-cost pricing is allowed; the row shows a non-blocking warning.
      const requestPayload = operation === 'price'
        ? { ...payload, acknowledgePolicyWarnings: true }
        : payload;
      const response = await updateQuickPriceListFormat(branchId, format.id, requestPayload);
      if (!isCurrentRequest()) return false;
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      const policyContextIsCurrent = operation !== 'price'
        || policyContextVersion === undefined
        || policyContextVersion
        === (policyContextVersionsRef.current[String(format.id)] || 0);
      const currentFormat = findCatalogFormat(catalogRef.current, format.id) || latestFormat;
      if (operation === 'price') bumpPolicyContextVersion(format.id);
      applyFormatResponse(format.id, response, {
        sellingPrice: payload.sellingPrice ?? currentFormat.sellingPrice,
        isAvailable: payload.isAvailable ?? currentFormat.isAvailable,
      }, {
        operation,
        includeAnalysis: policyContextIsCurrent,
        includeAvailability: operation === 'price' && !currentFormat.isConfigured,
      });
      if (operation === 'price' && !policyContextIsCurrent) {
        void loadCatalog({ silent: true, event: { priceIds: [format.id] } });
      }
      setNotice(operation === 'availability'
        ? 'Disponibilidad actualizada.'
        : 'Precio guardado.');
      return true;
    } catch (error) {
      if (!isCurrentRequest()) return false;
      if (operation === 'price'
        && draftVersion !== undefined
        && draftVersion !== (priceDraftVersionsRef.current[format.id] || 0)) {
        return false;
      }
      if (operation === 'price' && extractPolicyWarning(error)
        && policyContextVersion !== undefined
        && policyContextVersion !== (policyContextVersionsRef.current[String(format.id)] || 0)) {
        return false;
      }
      const message = resolveErrorMessage(
        error,
        operation === 'availability'
          ? 'No se pudo actualizar la disponibilidad.'
          : 'No se pudo guardar el precio.'
      );
      setErrorsByFormat((current) => ({ ...current, [format.id]: message }));
      return false;
    } finally {
      if (isCurrentRequest()) {
        setSaving((current) => ({ ...current, [format.id]: false }));
      }
    }
  }, [
    applyFormatResponse,
    branchId,
    bumpPolicyContextVersion,
    canUpdate,
    loadCatalog,
  ]);

  const handleSavePrice = useCallback(async ({ product, variety, format, sellingPrice }) => {
    const formatId = String(format.id);
    const current = findCatalogFormat(catalogRef.current, formatId);
    if (isPresent(current?.sellingPrice) && Number(current.sellingPrice) === sellingPrice) return true;
    const draftVersion = priceDraftVersionsRef.current[formatId] || 0;
    const saved = await performFormatUpdate({
      product,
      variety,
      format,
      payload: { sellingPrice },
      operation: 'price',
      draftVersion,
      policyContextVersion: policyContextVersionsRef.current[formatId] || 0,
    });
    return saved;
  }, [performFormatUpdate]);

  const handlePriceDraftChange = useCallback((formatId) => {
    const key = String(formatId);
    priceDraftVersionsRef.current[key] = (
      priceDraftVersionsRef.current[key] || 0
    ) + 1;
    setErrorsByFormat((current) => {
      if (!current[key]) return current;
      return { ...current, [key]: '' };
    });
  }, []);

  const handleFormatFieldDirtyChange = useCallback((formatId, field, dirty) => {
    const key = `${String(formatId)}:${field}`;
    const nextDirty = Boolean(dirty);
    if (Boolean(dirtyFormatFieldsRef.current[key]) === nextDirty) return;
    const next = { ...dirtyFormatFieldsRef.current };
    if (nextDirty) next[key] = true;
    else delete next[key];
    dirtyFormatFieldsRef.current = next;
    setDirtyFormatFields(next);
  }, []);

  const performFormatSettingsUpdate = useCallback(async ({
    format,
    payload,
    operation,
  }) => {
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    const isCurrentRequest = () => activeBranchIdRef.current === requestBranchId
      && branchGenerationRef.current === requestGeneration;
    if (!canUpdate || !requestBranchId || activeBranchIdRef.current !== requestBranchId) {
      return false;
    }
    const latestFormat = findCatalogFormat(catalogRef.current, format.id) || format;
    if (operation === 'rule' && latestFormat.isPriceLocked) {
      setErrorsByFormat((current) => ({
        ...current,
        [format.id]: 'Desbloquea este formato para cambiar la regla de precio.',
      }));
      return false;
    }
    if (['cost', 'rule'].includes(operation)
      && bulkMutationRef.current.formatIds.size > 0
      && bulkMutationRef.current.formatIds.has(String(format.id))) return false;
    if (operation === 'rule'
      && priceLockMutationRef.current?.formatIds.has(String(format.id))) return false;
    if (formatRecalculationOperationsRef.current.has(String(format.id))) return false;
    const nextRule = normalizePriceRuleSelection(payload.branch?.priceRule, payload.branch?.stockPriceRule);
    const sameValue = operation === 'cost'
      ? (latestFormat.shared?.manualBaseCost ?? latestFormat.manualBaseCost ?? null) === payload.shared?.manualBaseCost
      : JSON.stringify(normalizePriceRuleSelection(latestFormat.priceRule, latestFormat.stockPriceRule)) === JSON.stringify(nextRule);
    if (sameValue) return true;
    const savingKey = `${format.id}:${operation}`;
    const mutationVersionKey = String(format.id);
    const mutationVersion = (settingsMutationVersionsRef.current[mutationVersionKey] || 0) + 1;
    settingsMutationVersionsRef.current[mutationVersionKey] = mutationVersion;
    catalogRequestIdRef.current += 1;
    setRefreshing(false);
    const requestPolicyContextVersion = invalidatePolicyContext(format.id);
    setSavingSettings((current) => ({ ...current, [savingKey]: true }));
    setErrorsByFormat((current) => ({ ...current, [format.id]: '' }));
    setPageError('');
    try {
      const completeMissingPrice = !isPresent(latestFormat.sellingPrice) && !latestFormat.isPriceLocked
        && (operation === 'cost' || nextRule)
        && !RECALCULATION_DIRTY_FIELDS.some((field) => field !== operation
          && dirtyFormatFieldsRef.current[`${format.id}:${field}`]);
      const response = await updatePriceListFormatSettings(branchId, format.id, {
        ...payload, ...(completeMissingPrice ? { completeMissingPrice: true } : {}),
      });
      if (!isCurrentRequest()) return false;
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      const policyContextWasStable = requestPolicyContextVersion
        === (policyContextVersionsRef.current[mutationVersionKey] || 0);
      invalidatePolicyContext(format.id);
      const isLatestSettingsMutation = mutationVersion
        === settingsMutationVersionsRef.current[mutationVersionKey];
      const currentFormat = findCatalogFormat(catalogRef.current, format.id) || format;
      const resolvedUpdates = resolveQuickFormatSettingsUpdates(
        response,
        { format: currentFormat, payload, operation }
      );
      const updates = { ...resolvedUpdates };
      if (!isLatestSettingsMutation && operation === 'rule') {
        delete updates.priceRule;
        delete updates.stockPriceRule;
        delete updates.priceRuleAssignment;
      }
      if (!isLatestSettingsMutation || !policyContextWasStable) {
        delete updates.costs;
        delete updates.analysis;
      }
      const next = updateFormatInCatalog(catalogRef.current, format.id, updates);
      catalogRef.current = next;
      setCatalog(next);
      if (!isLatestSettingsMutation || !policyContextWasStable) {
        void loadCatalog({ silent: true, event: { priceIds: [format.id] } });
      }
      if (!payload.branch?.autoAssignRule || response.branch?.priceRuleAssignment?.type === 'most_used') {
        setNotice(payload.branch?.autoAssignRule ? 'Regla más usada aplicada automáticamente. Revisa el aviso junto a disponibilidad.'
          : operation === 'cost' ? 'Costo actualizado.' : 'Regla guardada automáticamente.');
      }
      return true;
    } catch (error) {
      if (!isCurrentRequest()) return false;
      setErrorsByFormat((current) => ({
        ...current,
        [format.id]: resolveErrorMessage(
          error,
          operation === 'cost'
            ? 'No se pudo guardar el costo.'
            : 'No se pudo guardar la regla de precio.'
        ),
      }));
      return false;
    } finally {
      if (isCurrentRequest()) {
        setSavingSettings((current) => ({ ...current, [savingKey]: false }));
      }
    }
  }, [branchId, canUpdate, invalidatePolicyContext, loadCatalog]);

  const handleSaveCost = useCallback(async ({ product, variety, format, manualBaseCost }) => {
    const key = String(format.id);
    const currentFormat = findCatalogFormat(catalogRef.current, format.id) || format;
    const breakdown = currentFormat.costs?.referenceCostBreakdown;
    const sourceStockListId = breakdown?.sourceStockListId
      ?? currentFormat.costs?.sourceStockListId
      ?? null;
    const calculatedCost = currentFormat.costs?.calculatedCost;
    const hasEditableStandardSource = Boolean(
      getEditableStandardStockCostSource(currentFormat.costs)
    );
    const hasCalculatedImportSource = breakdown?.listType !== 'standard'
      && (currentFormat.costs?.costSource || currentFormat.costs?.referenceCostSource) === 'stock_list'
      && manualBaseCost !== null
      && sourceStockListId !== null
      && sourceStockListId !== undefined
      && calculatedCost !== null
      && calculatedCost !== undefined
      && calculatedCost !== ''
      && Number.isFinite(Number(calculatedCost))
      && Array.isArray(breakdown?.rows)
      && breakdown.rows.some((row) => row?.stockId !== null && row?.stockId !== undefined);
    const hasCalculatedSource = hasEditableStandardSource || hasCalculatedImportSource;

    if (hasCalculatedSource) {
      if (pendingCostCommitRef.current || !canUpdate) return false;
      if (!canCorrectStockCosts) {
        setErrorsByFormat((current) => ({
          ...current,
          [format.id]: 'Necesitas el permiso stock.update para corregir el costo calculado.',
        }));
        return false;
      }
      const requestBranchId = String(branchId || '');
      const requestGeneration = branchGenerationRef.current;
      if (!requestBranchId || activeBranchIdRef.current !== requestBranchId
        || catalogBranchIdRef.current !== requestBranchId) return false;
      const policyContextVersion = invalidatePolicyContext(format.id);
      setErrorsByFormat((current) => ({ ...current, [format.id]: '' }));
      setPageError('');
      return new Promise((resolve) => {
        const pending = {
          branchId: requestBranchId,
          generation: requestGeneration,
          formatId: key,
          product,
          variety,
          format: currentFormat,
          manualBaseCost,
          policyContextVersion,
          resolve,
        };
        pendingCostCommitRef.current = pending;
        setCostOverrideReview(pending);
      });
    }

    const saved = await performFormatSettingsUpdate({
      format: currentFormat,
      operation: 'cost',
      payload: { shared: { manualBaseCost } },
    });
    return saved;
  }, [
    branchId,
    canCorrectStockCosts,
    canUpdate,
    invalidatePolicyContext,
    performFormatSettingsUpdate,
  ]);

  const handleEditCalculatedCost = useCallback(({ product, variety, format }) => {
    const key = String(format.id);
    const currentFormat = findCatalogFormat(catalogRef.current, format.id) || format;
    const costs = currentFormat.costs || {};
    const editableStandardStockCostSource = getEditableStandardStockCostSource(costs);
    if (pendingCostCommitRef.current || !canUpdate || !editableStandardStockCostSource) return false;
    if (!canCorrectStockCosts) {
      setErrorsByFormat((current) => ({
        ...current,
        [format.id]: 'Necesitas el permiso stock.update para corregir el costo calculado.',
      }));
      return false;
    }
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    if (!requestBranchId || activeBranchIdRef.current !== requestBranchId
      || catalogBranchIdRef.current !== requestBranchId) return false;
    const pending = {
      branchId: requestBranchId,
      generation: requestGeneration,
      formatId: key,
      product,
      variety,
      format: currentFormat,
      manualBaseCost: null,
      policyContextVersion: invalidatePolicyContext(format.id),
      resolve: () => {},
    };
    setErrorsByFormat((current) => ({ ...current, [format.id]: '' }));
    setPageError('');
    pendingCostCommitRef.current = pending;
    setCostOverrideReview(pending);
    return true;
  }, [branchId, canCorrectStockCosts, canUpdate, invalidatePolicyContext]);

  const handleCancelCostOverride = useCallback(() => {
    const pending = pendingCostCommitRef.current;
    if (!pending) return;
    pendingCostCommitRef.current = null;
    setCostOverrideReview(null);
    pending.resolve(false);
  }, []);

  const assertCurrentCostOverride = useCallback(() => {
    const pending = pendingCostCommitRef.current;
    if (!pending
      || pending.branchId !== activeBranchIdRef.current
      || pending.generation !== branchGenerationRef.current
      || pending.policyContextVersion
        !== (policyContextVersionsRef.current[pending.formatId] || 0)) {
      const error = new Error('El costo de origen cambió. Cierra esta revisión y vuelve a intentarlo.');
      error.code = 'stale_cost_override';
      throw error;
    }
    return pending;
  }, []);

  const handlePreviewCostOverride = useCallback(async (payload) => {
    const pending = assertCurrentCostOverride();
    const response = await previewQuickPriceCostOverride(
      pending.branchId,
      pending.formatId,
      payload
    );
    const current = assertCurrentCostOverride();
    if (current !== pending
      || String(response?.varietyPriceId) !== pending.formatId
      || String(response?.sourceStockListId) !== String(payload.sourceStockListId)) {
      throw new Error('La vista previa no corresponde al formato seleccionado.');
    }
    return response;
  }, [assertCurrentCostOverride]);

  const handleApplyCostOverride = useCallback(async (payload) => {
    const pending = assertCurrentCostOverride();
    catalogRequestIdRef.current += 1;
    setRefreshing(false);
    const response = await applyQuickPriceCostOverride(
      pending.branchId,
      pending.formatId,
      payload
    );
    const current = pendingCostCommitRef.current;
    if (current !== pending
      || pending.branchId !== activeBranchIdRef.current
      || pending.generation !== branchGenerationRef.current
      || String(response?.varietyPriceId) !== pending.formatId) {
      throw new Error('La respuesta no corresponde al formato seleccionado.');
    }

    catalogRequestIdRef.current += 1;
    setRefreshing(false);
    settingsMutationVersionsRef.current[pending.formatId] = (
      settingsMutationVersionsRef.current[pending.formatId] || 0
    ) + 1;
    const latestFormat = findCatalogFormat(catalogRef.current, pending.formatId) || pending.format;
    const selectedManualCost = payload.choice === 'manual' ? payload.manualBaseCost : null;
    const updates = resolveQuickFormatSettingsUpdates(response, {
      format: latestFormat,
      payload: { shared: { manualBaseCost: selectedManualCost } },
      operation: 'cost',
    });
    const nextCatalog = updateFormatInCatalog(catalogRef.current, pending.formatId, updates);
    catalogRef.current = nextCatalog;
    setCatalog(nextCatalog);
    invalidatePolicyContext(pending.formatId);
    handleFormatFieldDirtyChange(pending.formatId, 'cost', false);
    setErrorsByFormat((currentErrors) => ({ ...currentErrors, [pending.formatId]: '' }));
    setCostResetSignals((signals) => ({
      ...signals,
      [pending.formatId]: (signals[pending.formatId] || 0) + 1,
    }));

    pendingCostCommitRef.current = null;
    setCostOverrideReview(null);
    const editCount = Number(response?.costEditCount || 0);
    setNotice(payload.choice === 'recommended'
      ? `Costo recomendado aplicado${editCount ? ` con ${editCount} ${editCount === 1 ? 'corrección' : 'correcciones'} de stock` : ''}.`
      : `Costo manual conservado${editCount ? ` y ${editCount} ${editCount === 1 ? 'corrección agregada' : 'correcciones agregadas'} al stock` : ''}.`);
    pending.resolve(false);
    void loadCatalog({ silent: true, event: { priceIds: [pending.formatId] } });
    return response;
  }, [
    assertCurrentCostOverride,
    handleFormatFieldDirtyChange,
    invalidatePolicyContext,
    loadCatalog,
  ]);

  const handleSaveRule = useCallback(async ({ format, priceRule, stockPriceRule }) => {
    const selection = normalizePriceRuleSelection(priceRule, stockPriceRule);
    const branchPayload = selection?.type === 'complex'
      ? { priceRule: selection }
      : {
          stockPriceRule: selection?.type === 'simple'
            ? { mode: selection.rule.mode, value: selection.rule.value }
            : null,
        };
    const saved = await performFormatSettingsUpdate({
      format,
      operation: 'rule',
      payload: { branch: branchPayload },
    });
    return saved;
  }, [performFormatSettingsUpdate]);

  const performFormatRecalculation = useCallback(async ({ product, format }) => {
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    const formatId = String(format.id);
    const currentFormat = findCatalogFormat(catalogRef.current, formatId);
    if (!canUpdate || !requestBranchId || activeBranchIdRef.current !== requestBranchId
      || catalogBranchIdRef.current !== requestBranchId
      || !recalculationReadyFormats[formatId] || !currentFormat?.isConfigured
      || !hasPriceRuleSelection(currentFormat.priceRule, currentFormat.stockPriceRule)
      || currentFormat.productFormatActive === false
      || dirtyFormatFieldsRef.current[`${formatId}:cost`]
      || dirtyFormatFieldsRef.current[`${formatId}:rule`]
      || dirtyFormatFieldsRef.current[`${formatId}:price`]
      || savingPrices[formatId] || savingSettings[`${formatId}:cost`]
      || savingSettings[`${formatId}:rule`] || savingAvailability[formatId]
      || savingProducts[product?.id] || savingPromotions[formatId]
      || bulkMutationRef.current.formatIds.size > 0
      || priceLockMutationRef.current
      || promotionMutationsRef.current.has(formatId)
      || formatRecalculationOperationsRef.current.has(formatId)) return false;

    const operation = {};
    formatRecalculationOperationsRef.current.set(formatId, operation);
    const isCurrentOperation = () => activeBranchIdRef.current === requestBranchId
      && branchGenerationRef.current === requestGeneration
      && formatRecalculationOperationsRef.current.get(formatId) === operation;
    priceDraftVersionsRef.current[formatId] = (priceDraftVersionsRef.current[formatId] || 0) + 1;
    const requestPolicyContextVersion = invalidatePolicyContext(formatId);
    setPriceResetSignals((current) => ({
      ...current,
      [formatId]: (current[formatId] || 0) + 1,
    }));
    setRecalculatingFormats((current) => ({ ...current, [formatId]: true }));
    setErrorsByFormat((current) => ({ ...current, [formatId]: '' }));
    setPageError('');
    const payload = { formatIds: [format.id] };
    const policyContextIsCurrent = () => requestPolicyContextVersion
      === (policyContextVersionsRef.current[formatId] || 0);
    const canAcknowledgeCurrentRequest = () => isCurrentOperation() && policyContextIsCurrent();

    let focusPriceAfterCompletion = false;
    let catalogRefreshAfterCompletion = null;
    try {
      let response;
      try {
        response = await recalculateQuickPriceListFormats(branchId, payload);
      } catch (error) {
        const warning = extractPolicyWarning(error);
        if (!warning) throw error;
        if (!canAcknowledgeCurrentRequest()) return false;
        if (!/^[a-f0-9]{64}$/.test(warning.policyWarningToken || '')) throw error;
        response = await recalculateQuickPriceListFormats(branchId, {
          ...payload,
          acknowledgePolicyWarnings: true,
          policyWarningToken: warning.policyWarningToken,
        });
      }
      if (!isCurrentOperation()) return false;
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      const responseFormats = Array.isArray(response?.formats)
        ? response.formats
        : Array.isArray(response?.data?.formats) ? response.data.formats : [];
      const responseFormat = responseFormats.find((entry) => String(
        entry?.varietyPriceId ?? entry?.format?.varietyPriceId ?? entry?.id
      ) === formatId);
      const skippedEntries = Array.isArray(response?.skipped) ? response.skipped : [];
      const skipped = skippedEntries.find((entry) => String(entry?.varietyPriceId) === formatId);
      if (!responseFormat) {
        if (skipped) {
          setErrorsByFormat((current) => ({
            ...current,
            [formatId]: `No se recalculó: ${skipped.message || 'faltan datos para aplicar la regla.'}`,
          }));
          return false;
        }
        throw new Error('No se recibió el precio recalculado. Actualiza la lista e intenta nuevamente.');
      }

      const contextStayedCurrent = policyContextIsCurrent();
      const latestFormat = findCatalogFormat(catalogRef.current, formatId) || currentFormat;
      const updates = resolveBulkFormatUpdates(responseFormat, latestFormat, {
        includePrice: true,
        includeRule: true,
      });
      if (!contextStayedCurrent) {
        delete updates.costs;
        delete updates.analysis;
      }
      const nextCatalog = updateFormatInCatalog(catalogRef.current, formatId, updates);
      catalogRef.current = nextCatalog;
      setCatalog(nextCatalog);
      setPriceResetSignals((current) => ({
        ...current,
        [formatId]: (current[formatId] || 0) + 1,
      }));
      catalogRefreshAfterCompletion = loadCatalog({ silent: true, event: { priceIds: [formatId] } });
      if (!isCurrentOperation()) return false;

      if (!contextStayedCurrent) {
        setErrorsByFormat((current) => ({
          ...current,
          [formatId]: 'El precio se recalculó, pero el costo o la regla cambió nuevamente. Vuelve a recalcular.',
        }));
        return false;
      }
      const updatedCount = Number(response?.updatedCount || 0);
      setNotice(updatedCount > 0
        ? 'Precio recalculado y aplicado.'
        : 'El precio ya estaba actualizado.');
      focusPriceAfterCompletion = true;
      return true;
    } catch (error) {
      if (!isCurrentOperation()) return false;
      const message = extractPolicyWarning(error)
        ? 'Los costos o reglas cambiaron. Revisa los valores y vuelve a intentar.'
        : resolveErrorMessage(error, 'No se pudo recalcular este precio. Intenta nuevamente.');
      setErrorsByFormat((current) => ({ ...current, [formatId]: message }));
      if (extractPolicyWarning(error)) void loadCatalog({ silent: true, event: { priceIds: [formatId] } });
      return false;
    } finally {
      if (isCurrentOperation()) {
        formatRecalculationOperationsRef.current.delete(formatId);
        setRecalculatingFormats((current) => {
          const next = { ...current };
          delete next[formatId];
          return next;
        });
        if (focusPriceAfterCompletion) {
          const focusRequest = {
            branchId: requestBranchId,
            generation: requestGeneration,
            formatId,
          };
          pendingPriceFocusRef.current = focusRequest;
          const focusPrice = () => {
            if (pendingPriceFocusRef.current === focusRequest
              && activeBranchIdRef.current === requestBranchId
              && branchGenerationRef.current === requestGeneration) {
              const priceInput = document.getElementById(`quick-price-${formatId}`);
              if (priceInput && !priceInput.disabled) priceInput.focus();
            }
          };
          window.setTimeout(focusPrice, 0);
          Promise.resolve(catalogRefreshAfterCompletion).finally(() => {
            window.setTimeout(() => {
              focusPrice();
              if (pendingPriceFocusRef.current === focusRequest) {
                pendingPriceFocusRef.current = null;
              }
            }, 0);
          });
        }
      }
    }
  }, [
    branchId,
    canUpdate,
    invalidatePolicyContext,
    loadCatalog,
    recalculationReadyFormats,
    savingAvailability,
    savingPromotions,
    savingPrices,
    savingProducts,
    savingSettings,
  ]);

  const performPromotionSave = useCallback(async (pending, payload) => {
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    const replacing = Boolean(pending?.promotion?.id);
    const creatingOffer = payload?.mode === 'offer';
    const targetIds = replacing
      ? [pending?.promotion?.varietyPriceId || pending?.targets?.[0]?.id]
      : payload?.varietyPriceIds;
    const formatIds = Array.from(new Set((targetIds || [])
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(String)));
    if (!canUpdate || !pending || !formatIds.length || !requestBranchId
      || activeBranchIdRef.current !== requestBranchId
      || catalogBranchIdRef.current !== requestBranchId
      || bulkMutationRef.current.formatIds.size > 0
      || formatIds.some((formatId) => (
        promotionMutationsRef.current.has(formatId)
        || formatRecalculationOperationsRef.current.has(formatId)
        || !findCatalogFormat(catalogRef.current, formatId)?.isConfigured
        || isOfferFormat(findCatalogFormat(catalogRef.current, formatId))
      ))) return false;

    if (!creatingOffer && payload.type === 'unit_price'
      && formatIds.some((id) => isDetailPromotionTarget(findCatalogFormat(catalogRef.current, id)))) {
      setPromotionDialogError('Los descuentos unitarios de Detalle se crean con el formato Oferta.');
      return false;
    }

    const operation = {};
    formatIds.forEach((formatId) => promotionMutationsRef.current.set(formatId, operation));
    const isCurrentOperation = () => activeBranchIdRef.current === requestBranchId
      && branchGenerationRef.current === requestGeneration
      && formatIds.every((formatId) => promotionMutationsRef.current.get(formatId) === operation);
    setSavingPromotions((current) => {
      const next = { ...current };
      formatIds.forEach((formatId) => { next[formatId] = true; });
      return next;
    });
    setPromotionErrors((current) => {
      const next = { ...current };
      formatIds.forEach((formatId) => { next[formatId] = ''; });
      return next;
    });
    setPromotionDialogError('');

    try {
      const terms = { ...payload };
      delete terms.varietyPriceIds;
      delete terms.branchIds;
      delete terms.mode;
      delete terms.branchLimits;
      delete terms.branchPrices;
      if (replacing && payload.branchLimits?.[branchId]) Object.assign(terms, payload.branchLimits[branchId]);
      if (replacing && payload.branchPrices?.[branchId] != null) terms.promotionPrice = payload.branchPrices[branchId];
      const branchResults = [];
      const branchFailures = [];
      let response;
      if (replacing) {
        response = await replacePriceListPromotion(branchId, pending.promotion.id, terms);
      } else {
        const { branchIds = [branchId], branchLimits = {}, branchPrices = {}, mode: _mode, ...commonPayload } = payload;
        for (const targetBranchId of Array.from(new Set(branchIds.map(String)))) {
          try {
            const branchPayload = { ...commonPayload, ...(branchLimits[targetBranchId] || {}),
              ...(branchPrices[targetBranchId] != null ? (creatingOffer
                ? { sellingPrice: branchPrices[targetBranchId], useOfferRule: false }
                : { promotionPrice: branchPrices[targetBranchId] }) : {}) };
            const result = creatingOffer ? await createPriceListOffers(targetBranchId, branchPayload)
              : await runPartialPricingBulk(`price-promotions:${targetBranchId}`, branchPayload,
                (data) => createPriceListPromotions(targetBranchId, data));
            const records = creatingOffer ? result?.offers : result?.promotions;
            if (!Array.isArray(records) || (!records.length && !result.operationId)) {
              throw new Error('No se recibieron los precios temporales creados. Actualiza la lista e intenta nuevamente.');
            }
            branchResults.push({ branchId: targetBranchId, result });
          } catch (error) {
            branchFailures.push({ branchId: targetBranchId, error });
          }
        }
        if (creatingOffer) {
          if (!isCurrentOperation()) return false;
          const label = (id) => promotionBranchOptions.find((branch) => String(branch.id) === String(id))?.branchName || `Sucursal ${id}`;
          const saved = branchResults.map(({ branchId: id, result }) => `${label(id)}: ${result.offers.length} ${result.offers.length === 1 ? 'Oferta activada' : 'Ofertas activadas'}.`).join('\n');
          if (saved) setNotice(saved);
          if (branchResults.length) {
            await loadCatalog({ silent: true });
            if (!isCurrentOperation()) return false;
            setBulkRefresh((current) => current + 1);
          }
          if (branchFailures.length) {
            setPromotionDialogError([saved, ...branchFailures.map(({ branchId: id, error }) => `${label(id)}: ${resolveErrorMessage(error, 'No se pudo crear la Oferta.')}`),
              'Quedaron seleccionadas las sucursales pendientes para reintentar.'].filter(Boolean).join('\n'));
            return { failedBranchIds: branchFailures.map((failure) => failure.branchId),
              warnings: branchFailures.flatMap(({ branchId: id, error }) => error?.response?.data?.code === 'pricing_policy_confirmation_required'
                ? (error.response.data.warnings || []).map((warning) => `${label(warning.branchId || id)}: ${warning.message || warning.label || warning.code}`) : []) };
          }
          setPromotionDialog(null);
          return true;
        }
        response = {
          promotions: branchResults.flatMap(({ branchId: targetBranchId, result }) => (
            result.promotions.map((promotion) => ({ ...promotion, branchId: targetBranchId }))
          )),
          operationId: branchResults.some(({ result }) => result.operationId),
        };
      }
      if (!isCurrentOperation()) return false;
      const responsePromotions = replacing
        ? [response?.promotion]
        : response?.promotions;
      if (!Array.isArray(responsePromotions) || (!responsePromotions.length && !response?.operationId && !branchFailures.length)) {
        throw new Error('No se recibieron los precios temporales creados. Actualiza la lista e intenta nuevamente.');
      }

      let nextCatalog = catalogRef.current;
      responsePromotions.filter(Boolean).forEach((rawPromotion, index) => {
        if (rawPromotion.branchId && String(rawPromotion.branchId) !== requestBranchId) return;
        const fallbackFormatId = formatIds[index] || formatIds[0];
        const promotion = normalizePriceListPromotion({
          ...rawPromotion,
          varietyPriceId: rawPromotion.varietyPriceId || fallbackFormatId,
        });
        const formatId = String(promotion.varietyPriceId);
        nextCatalog = updatePromotionsForFormat(nextCatalog, formatId, (current) => [
          ...current.filter((entry) => !replacing
            || String(entry.id) !== String(pending.promotion.id)),
          promotion,
        ]);
      });
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      catalogRef.current = nextCatalog;
      setCatalog(nextCatalog);
      setBulkRefresh((current) => current + 1);
      const branchLabel = (id) => promotionBranchOptions.find((branch) => String(branch.id) === String(id))?.branchName || `Sucursal ${id}`;
      const savedSummary = replacing ? 'Se creó una nueva versión del precio temporal.'
        : branchResults.map(({ branchId: targetBranchId, result }) => `${branchLabel(targetBranchId)}: ${result.operationId
          ? bulkPricingSummary(result)
          : `${result.promotions.length} ${result.promotions.length === 1 ? 'precio temporal creado' : 'precios temporales creados'}.`}`).join('\n');
      if (savedSummary) setNotice(savedSummary);
      if (branchFailures.length) {
        setPromotionDialogError([
          savedSummary,
          ...branchFailures.map(({ branchId: targetBranchId, error }) => `${branchLabel(targetBranchId)}: ${resolveErrorMessage(error, 'No se pudieron crear los precios temporales.')}`),
          'Quedaron seleccionadas las sucursales que no se pudieron guardar para reintentar.',
        ].filter(Boolean).join('\n'));
        if (branchFailures.some((failure) => failure.branchId === requestBranchId && failure.error?.response?.status === 409)) {
          void loadCatalog({ silent: true, event: { priceIds: formatIds } });
        }
        return { failedBranchIds: branchFailures.map((failure) => failure.branchId) };
      }
      setPromotionDialog(null);
      return true;
    } catch (error) {
      if (!isCurrentOperation()) return false;
      setPromotionDialogError(resolveErrorMessage(
        error,
        replacing ? 'No se pudo reemplazar el precio temporal.' : 'No se pudieron crear los precios temporales.'
      ));
      if (error?.response?.status === 409) void loadCatalog({ silent: true, event: { priceIds: formatIds } });
      return false;
    } finally {
      if (isCurrentOperation()) {
        formatIds.forEach((formatId) => promotionMutationsRef.current.delete(formatId));
        setSavingPromotions((current) => {
          const next = { ...current };
          formatIds.forEach((formatId) => { delete next[formatId]; });
          return next;
        });
      }
    }
  }, [branchId, canUpdate, loadCatalog, promotionBranchOptions]);

  const performPromotionEnd = useCallback(async (pending) => {
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    const formatId = String(pending?.promotion?.varietyPriceId || pending?.format?.id || '');
    if (!canUpdate || !pending?.promotion?.id || !formatId || !requestBranchId
      || activeBranchIdRef.current !== requestBranchId
      || catalogBranchIdRef.current !== requestBranchId
      || promotionMutationsRef.current.has(formatId)) return false;

    const operation = {};
    promotionMutationsRef.current.set(formatId, operation);
    const isCurrentOperation = () => activeBranchIdRef.current === requestBranchId
      && branchGenerationRef.current === requestGeneration
      && promotionMutationsRef.current.get(formatId) === operation;
    setSavingPromotions((current) => ({ ...current, [formatId]: true }));
    setPromotionErrors((current) => ({ ...current, [formatId]: '' }));
    try {
      await endPriceListPromotion(branchId, pending.promotion.id);
      if (!isCurrentOperation()) return false;
      const nextCatalog = updatePromotionsForFormat(catalogRef.current, formatId, (current) => (
        current.filter((promotion) => String(promotion.id) !== String(pending.promotion.id))
      ));
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      catalogRef.current = nextCatalog;
      setCatalog(nextCatalog);
      setPromotionConfirmation(null);
      setNotice(getPromotionState(pending.promotion, promotionNow) === 'scheduled'
        ? 'Precio temporal programada cancelada.'
        : 'Precio temporal finalizada.');
      return true;
    } catch (error) {
      if (!isCurrentOperation()) return false;
      setPromotionErrors((current) => ({
        ...current,
        [formatId]: resolveErrorMessage(error, 'No se pudo finalizar el precio temporal.'),
      }));
      setPromotionConfirmation(null);
      if (error?.response?.status === 409) void loadCatalog({ silent: true, event: { priceIds: [formatId] } });
      return false;
    } finally {
      if (isCurrentOperation()) {
        promotionMutationsRef.current.delete(formatId);
        setSavingPromotions((current) => {
          const next = { ...current };
          delete next[formatId];
          return next;
        });
      }
    }
  }, [branchId, canUpdate, loadCatalog, promotionNow]);

  const openOfferAction = (target, end = false) => {
    if (!canUpdate || catalogBranchIdRef.current !== String(branchId) || !target.format?.offerAvailability) return;
    setOfferActionError('');
    setOfferAction({ ...target, end, branchId: String(branchId), generation: branchGenerationRef.current });
  };

  const performOfferAction = async (payload) => {
    const pending = offerAction;
    if (!pending || !canUpdate || pending.branchId !== activeBranchIdRef.current
      || pending.generation !== branchGenerationRef.current || catalogBranchIdRef.current !== pending.branchId) return;
    const formatId = String(pending.format.id);
    if (promotionMutationsRef.current.has(formatId) || formatRecalculationOperationsRef.current.has(formatId)
      || bulkMutationRef.current.formatIds.size || savingPrices[formatId] || savingAvailability[formatId]
      || savingSettings[`${formatId}:rule`] || savingLocks[formatId]) return;
    const operation = {};
    promotionMutationsRef.current.set(formatId, operation);
    const isCurrent = () => pending.branchId === activeBranchIdRef.current
      && pending.generation === branchGenerationRef.current && promotionMutationsRef.current.get(formatId) === operation;
    setSavingPromotions((current) => ({ ...current, [formatId]: true }));
    setOfferActionError('');
    try {
      const response = pending.end
        ? await endPriceListOffer(pending.branchId, pending.format.id, { expectedEnabledAt: pending.format.offerAvailability.enabledAt })
        : await editPriceListOffer(pending.branchId, pending.format.id, payload);
      if (!isCurrent()) return;
      catalogRequestIdRef.current += 1;
      const next = updateFormatInCatalog(catalogRef.current, formatId, {
        sellingPrice: response.sellingPrice, isAvailable: response.branch.isAvailable,
        offerAvailability: response.branch.offerAvailability });
      catalogRef.current = next;
      setCatalog(next);
      setOfferAction(null);
      setNotice(pending.end ? 'Oferta retirada de la venta.' : 'Oferta actualizada.');
      await loadCatalog({ silent: true, event: { priceIds: [pending.format.id] } });
    } catch (error) {
      if (isCurrent()) setOfferActionError(resolveErrorMessage(error, 'No se pudo actualizar la Oferta.'));
    } finally {
      if (isCurrent()) {
        promotionMutationsRef.current.delete(formatId);
        setSavingPromotions((current) => ({ ...current, [formatId]: false }));
      }
    }
  };

  const openPromotionDialog = useCallback(({ product, variety, format, promotion = null }) => {
    if (!canUpdate || !format?.isConfigured || isOfferFormat(format)) return;
    setPromotionDialogError('');
    setPromotionDialog({
      promotion,
      targets: getPromotionTargets(catalogRef.current, [format.id]),
    });
  }, [canUpdate]);

  const performPriceLockUpdate = useCallback(async ({ formats, isLocked }) => {
    const requestBranchId = String(branchId || '');
    const requestGeneration = branchGenerationRef.current;
    if (!canUpdate || !requestBranchId || activeBranchIdRef.current !== requestBranchId
      || catalogBranchIdRef.current !== requestBranchId) return false;

    const requestedIds = Array.from(new Set((Array.isArray(formats) ? formats : [])
      .flatMap((format) => (
        format?.id === null || format?.id === undefined ? [] : [String(format.id)]
      ))));
    const currentTargets = requestedIds.flatMap((formatId) => {
      const current = findCatalogFormat(catalogRef.current, formatId);
      return current && current.productFormatActive !== false
        && findCatalogFormat(visibleCatalogRef.current, formatId) ? [current] : [];
    });
    const targets = currentTargets.filter(
      (format) => Boolean(format.isPriceLocked) !== Boolean(isLocked)
    );
    const formatIds = targets.map((format) => format.id);
    if (!formatIds.length) {
      setNotice(isLocked
        ? 'Los formatos ya estaban bloqueados.'
        : 'Los formatos ya estaban desbloqueados.');
      return true;
    }
    if (formatIds.length > 1000) {
      setPageError('Filtra o divide la selección a un máximo de 1000 formatos.');
      return false;
    }
    const hasDirtyPricingDraft = formatIds.some((formatId) => (
      dirtyFormatFieldsRef.current[`${String(formatId)}:price`]
      || dirtyFormatFieldsRef.current[`${String(formatId)}:rule`]
    ));
    if (hasDirtyPricingDraft) {
      setPageError('Guarda o descarta los borradores de precio y regla antes de cambiar el bloqueo.');
      return false;
    }
    const hasPendingPricingMutation = formatIds.some((formatId) => (
      savingPrices[formatId]
      || savingSettings[`${formatId}:rule`]
      || recalculatingFormats[formatId]
      || formatRecalculationOperationsRef.current.has(String(formatId))
    ));
    if (hasPendingPricingMutation || savingBulk
      || bulkMutationRef.current.formatIds.size > 0 || priceLockMutationRef.current) {
      setPageError('Espera a que terminen los cambios de precio o regla antes de cambiar el bloqueo.');
      return false;
    }

    const operation = {
      branchId: requestBranchId,
      generation: requestGeneration,
      formatIds: new Set(formatIds.map(String)),
    };
    priceLockMutationRef.current = operation;
    const isCurrentOperation = () => activeBranchIdRef.current === requestBranchId
      && branchGenerationRef.current === requestGeneration
      && priceLockMutationRef.current === operation;
    catalogRequestIdRef.current += 1;
    setRefreshing(false);
    setSavingLocks((current) => {
      const next = { ...current };
      formatIds.forEach((formatId) => { next[formatId] = true; });
      return next;
    });
    setErrorsByFormat((current) => {
      const next = { ...current };
      formatIds.forEach((formatId) => { next[formatId] = ''; });
      return next;
    });
    setPageError('');

    try {
      const response = await runPartialPricingBulk(`price-lock:${branchId}`, { formatIds, isLocked: Boolean(isLocked) }, (payload) => updateQuickPriceListFormatLocks(branchId, payload));
      if (!isCurrentOperation()) return false;
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      const updates = resolvePriceLockUpdates(response, formatIds, isLocked);
      let nextCatalog = catalogRef.current;
      updates.forEach(({ formatId, isPriceLocked }) => {
        nextCatalog = updateFormatInCatalog(nextCatalog, formatId, { isPriceLocked });
      });
      catalogRef.current = nextCatalog;
      setCatalog(nextCatalog);

      const updatedIds = updates.map(({ formatId }) => String(formatId));
      setPriceResetSignals((current) => {
        const next = { ...current };
        updatedIds.forEach((formatId) => {
          next[formatId] = (next[formatId] || 0) + 1;
        });
        return next;
      });
      setRuleResetSignals((current) => {
        const next = { ...current };
        updatedIds.forEach((formatId) => {
          next[formatId] = (next[formatId] || 0) + 1;
        });
        return next;
      });
      const changedCount = Number(response?.changedCount ?? updates.length);
      const resolvedCount = Number.isFinite(changedCount) ? changedCount : updates.length;
      setBulkRefresh((current) => current + 1);
      setNotice(response?.operationId ? bulkPricingSummary(response) : `${resolvedCount} ${resolvedCount === 1 ? 'formato' : 'formatos'} ${
        isLocked ? (resolvedCount === 1 ? 'bloqueado' : 'bloqueados') : (resolvedCount === 1 ? 'desbloqueado' : 'desbloqueados')
      }.`);
      void loadCatalog({ silent: true, event: { priceIds: formatIds } });
      return true;
    } catch (error) {
      if (!isCurrentOperation()) return false;
      setPageError(resolveErrorMessage(
        error,
        isLocked
          ? 'No se pudieron bloquear los formatos.'
          : 'No se pudieron desbloquear los formatos.'
      ));
      if (error?.response?.status === 409) void loadCatalog({ silent: true, event: { priceIds: formatIds } });
      return false;
    } finally {
      if (isCurrentOperation()) {
        priceLockMutationRef.current = null;
        setSavingLocks((current) => {
          const next = { ...current };
          formatIds.forEach((formatId) => { delete next[formatId]; });
          return next;
        });
      }
    }
  }, [
    branchId,
    canUpdate,
    loadCatalog,
    recalculatingFormats,
    savingBulk,
    savingPrices,
    savingSettings,
  ]);

  const openBulkPromotionDialog = useCallback(() => {
    if (!canUpdate || !selectedFormatIds.size) return;
    if (Array.from(selectedFormatIds).some((id) => findCatalogFormat(catalogRef.current, id)?.isPriceLocked)) {
      setPageError('Desbloquea los formatos seleccionados antes de editarlos.');
      return;
    }
    if (selectedFormatIds.size > MAX_BULK_PROMOTION_COUNT) {
      setPageError(`Selecciona hasta ${MAX_BULK_PROMOTION_COUNT} formatos para crear precios temporales.`);
      return;
    }
    const targets = getPromotionTargets(catalogRef.current, selectedFormatIds);
    if (!targets.length) return;
    if (targets.some((target) => isOfferFormat(target.format))) {
      setPageError('Crea precios temporales desde Pack o Detalle. Oferta solo permite configurar su regla.');
      return;
    }
    setPageError('');
    setPromotionDialogError('');
    setPromotionDialog({ targets, promotion: null });
  }, [canUpdate, selectedFormatIds, selectedUnconfiguredCount]);

  const performBulkUpdate = useCallback(async (
    changes,
    {
      formatIds: requestedFormatIds,
      recalculate = false,
      scopeVersion = recalculationScopeVersionRef.current,
    } = {}
  ) => {
    const requestBranchId = String(branchId || '');
    if (!canUpdate || !hasRequiredFilters || !requestBranchId || activeBranchIdRef.current !== requestBranchId) {
      return false;
    }
    if (recalculate && (scopeVersion !== recalculationScopeVersionRef.current
      || anyMutationSaving || visibleRowsHaveErrors)) {
      setPageError('Espera a que terminen de guardarse los cambios y vuelve a recalcular.');
      return false;
    }
    if (recalculate && (!requestedFormatIds
      || [...requestedFormatIds].map(String).sort().join(',') !== visibleFormatSignature)) {
      setPageError('Los formatos visibles cambiaron. Revisa los filtros y vuelve a recalcular.');
      return false;
    }
    const selectionVersion = bulkSelectionVersionRef.current;
    const currentFormatIds = getCatalogFormatIds(visibleCatalogRef.current);
    const currentFormatIdSet = new Set(currentFormatIds.map((formatId) => String(formatId)));
    const formatIds = requestedFormatIds
      ? [...requestedFormatIds].filter((formatId) => currentFormatIdSet.has(String(formatId)))
      : currentFormatIds.filter((formatId) => selectedFormatIds.has(String(formatId)));
    if (!formatIds.length) {
      setPageError('Selecciona al menos un formato.');
      return false;
    }
    if (Object.hasOwn(changes, 'sellingPrice') && formatIds.some((id) => isOfferFormat(findCatalogFormat(catalogRef.current, id)))) {
      setPageError('El precio de Oferta se calcula con su regla. Selecciona Pack o Detalle para editar precios.');
      return false;
    }
    if (!recalculate && formatIds.some((id) => findCatalogFormat(catalogRef.current, id)?.isPriceLocked)) {
      setPageError('Desbloquea los formatos seleccionados antes de editarlos.');
      return false;
    }
    const recalculationDraftError = recalculate
      ? getRecalculationDraftError(dirtyFormatFieldsRef.current, formatIds)
      : '';
    if (recalculationDraftError) {
      setPageError(recalculationDraftError);
      return false;
    }
    const hasPendingSelectedMutation = formatIds.some((formatId) => (
      savingPrices[formatId]
      || savingSettings[`${formatId}:cost`]
      || savingSettings[`${formatId}:rule`]
    ));
    if (hasPendingSelectedMutation || bulkMutationRef.current.formatIds.size > 0
      || priceLockMutationRef.current
      || promotionMutationsRef.current.size > 0
      || formatRecalculationOperationsRef.current.size > 0) {
      setPageError('Espera a que terminen de guardarse los cambios individuales seleccionados.');
      return false;
    }

    const changesPrice = recalculate || Object.hasOwn(changes, 'sellingPrice');
    const changesRule = Object.hasOwn(changes, 'priceRule')
      || Object.hasOwn(changes, 'stockPriceRule');
    const payload = {
      formatIds,
      ...(changesPrice && !recalculate ? { sellingPrice: changes.sellingPrice } : {}),
      ...(Object.hasOwn(changes, 'priceRule') ? { priceRule: changes.priceRule } : {}),
      ...(Object.hasOwn(changes, 'stockPriceRule')
        ? { stockPriceRule: changes.stockPriceRule }
        : {}),
    };

    const operation = {
      formatIds: new Set(formatIds.map((formatId) => String(formatId))),
      price: changesPrice,
      rule: changesRule,
    };
    bulkMutationRef.current = operation;
    // A branch can be left and revisited while this request is pending. Only
    // its original owner may apply the response or release mutation locks.
    const isCurrentOperation = () => activeBranchIdRef.current === requestBranchId
      && bulkMutationRef.current === operation;
    catalogRequestIdRef.current += 1;
    setRefreshing(false);
    formatIds.forEach((formatId) => {
      if (changesPrice) {
        priceDraftVersionsRef.current[formatId] = (
          priceDraftVersionsRef.current[formatId] || 0
        ) + 1;
      }
      invalidatePolicyContext(formatId);
    });
    // Reset only fields replaced by this bulk operation. Other selected-row
    // drafts are paused while the request runs and resume autosaving afterward.
    if (changesPrice && !recalculate) {
      setPriceResetSignals((current) => {
        const next = { ...current };
        formatIds.forEach((formatId) => {
          next[formatId] = (next[formatId] || 0) + 1;
        });
        return next;
      });
    }
    if (changesRule) {
      setRuleResetSignals((current) => {
        const next = { ...current };
        formatIds.forEach((formatId) => {
          next[formatId] = (next[formatId] || 0) + 1;
        });
        return next;
      });
    }
    setErrorsByFormat((current) => {
      const next = { ...current };
      formatIds.forEach((formatId) => { next[formatId] = ''; });
      return next;
    });
    if (changesPrice) {
      setSavingPrices((current) => {
        const next = { ...current };
        formatIds.forEach((formatId) => { next[formatId] = true; });
        return next;
      });
    }
    if (changesRule) {
      setSavingSettings((current) => {
        const next = { ...current };
        formatIds.forEach((formatId) => { next[`${formatId}:rule`] = true; });
        return next;
      });
    }
    setSavingBulk(true);
    if (recalculate) {
      setRecalculating(true);
      setRecalculationResult(null);
    }
    setPageError('');

    const requestPolicyContextVersions = Object.fromEntries(formatIds.map((formatId) => [
      String(formatId), policyContextVersionsRef.current[String(formatId)] || 0,
    ]));
    const canAcknowledgeCurrentRequest = () => isCurrentOperation()
      && bulkSelectionVersionRef.current === selectionVersion
      && (!recalculate || scopeVersion === recalculationScopeVersionRef.current)
      && formatIds.every((formatId) => (
        requestPolicyContextVersions[String(formatId)]
        === (policyContextVersionsRef.current[String(formatId)] || 0)
      ));
    const sendRequest = (requestPayload) => recalculate
      ? recalculateQuickPriceListFormats(branchId, requestPayload)
      : updateQuickPriceListFormatsBulk(branchId, requestPayload);
    try {
      const response = await runPartialPricingBulk(`price-list:${branchId}:${recalculate ? 'recalculate' : 'update'}`, payload, sendRequest);
      if (!isCurrentOperation()) return false;
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      const responseFormats = Array.isArray(response?.formats)
        ? response.formats
        : Array.isArray(response?.data?.formats) ? response.data.formats : [];
      let nextCatalog = catalogRef.current;
      let needsFreshAnalysis = false;
      responseFormats.forEach((responseFormat) => {
        const formatId = responseFormat?.varietyPriceId
          ?? responseFormat?.format?.varietyPriceId
          ?? responseFormat?.id;
        if (formatId === null || formatId === undefined) return;
        const currentFormat = findCatalogFormat(nextCatalog, formatId) || {};
        const updates = resolveBulkFormatUpdates(responseFormat, currentFormat, {
          includePrice: changesPrice,
          includeRule: changesRule || recalculate,
        });
        if (requestPolicyContextVersions[String(formatId)]
          !== (policyContextVersionsRef.current[String(formatId)] || 0)) {
          delete updates.costs;
          delete updates.analysis;
          needsFreshAnalysis = true;
        }
        nextCatalog = updateFormatInCatalog(
          nextCatalog,
          formatId,
          updates
        );
      });
      catalogRef.current = nextCatalog;
      setCatalog(nextCatalog);
      if (changesPrice) {
        setPriceResetSignals((current) => {
          const next = { ...current };
          const resetFormatIds = recalculate
            ? responseFormats.map((format) => format.varietyPriceId ?? format.id).filter((id) => id != null)
            : response?.operationId ? responseFormats.map((format) => format.varietyPriceId ?? format.id).filter((id) => id != null) : formatIds;
          resetFormatIds.forEach((formatId) => {
            next[formatId] = (next[formatId] || 0) + 1;
          });
          return next;
        });
      }
      if (changesRule) {
        setRuleResetSignals((current) => {
          const next = { ...current };
          formatIds.forEach((formatId) => {
            next[formatId] = (next[formatId] || 0) + 1;
          });
          return next;
        });
      }
      if (!responseFormats.length || recalculate || needsFreshAnalysis) void loadCatalog({ silent: true, event: { priceIds: formatIds } }).catch(() => undefined);
      const updatedCount = Number(response?.updatedCount ?? responseFormats.length);
      const resolvedUpdatedCount = Number.isFinite(updatedCount) ? updatedCount : formatIds.length;
      if (recalculate && scopeVersion === recalculationScopeVersionRef.current) {
        const formatLabels = new Map(catalogRef.current.flatMap((product) => (
          product.varieties.flatMap((variety) => variety.formats.map((format) => [
            String(format.id),
            [product.name, variety.name, format.productFormatName].filter(Boolean).join(' · '),
          ]))
        )));
        setRecalculationResult({
          updatedCount: resolvedUpdatedCount,
          unchangedCount: Number(response?.unchangedCount || 0),
          skippedCount: Number(response?.skippedCount || 0),
          skipped: (Array.isArray(response?.skipped) ? response.skipped : []).map((entry) => ({
            ...entry,
            label: formatLabels.get(String(entry.varietyPriceId)) || `Formato ${entry.varietyPriceId}`,
          })),
        });
      }
      setBulkRefresh((current) => current + 1);
      setNotice(response?.operationId ? bulkPricingSummary(response) : recalculate
        ? `${resolvedUpdatedCount} precios recalculados y aplicados.`
        : `${resolvedUpdatedCount} ${resolvedUpdatedCount === 1 ? 'formato actualizado' : 'formatos actualizados'}.`);
      return true;
    } catch (error) {
      if (!isCurrentOperation()) return false;
      const warning = extractPolicyWarning(error);
      if (warning) {
        if (!canAcknowledgeCurrentRequest()) return false;
        setPageError('Los costos o reglas cambiaron. Revisa los valores y vuelve a intentar.');
        void loadCatalog({ silent: true, event: { priceIds: formatIds } });
        return false;
      }
      setPageError(resolveErrorMessage(error, recalculate
        ? 'No se pudieron recalcular los precios. Intenta nuevamente.'
        : 'No se pudieron aplicar los cambios masivos.'));
      return false;
    } finally {
      if (isCurrentOperation()) {
        bulkMutationRef.current = { formatIds: new Set(), price: false, rule: false };
        if (changesPrice) {
          setSavingPrices((current) => {
            const next = { ...current };
            formatIds.forEach((formatId) => { next[formatId] = false; });
            return next;
          });
        }
        if (changesRule) {
          setSavingSettings((current) => {
            const next = { ...current };
            formatIds.forEach((formatId) => { next[`${formatId}:rule`] = false; });
            return next;
          });
        }
        setSavingBulk(false);
        if (recalculate) setRecalculating(false);
      }
    }
  }, [
    branchId,
    canUpdate,
    anyMutationSaving,
    visibleRowsHaveErrors,
    visibleFormatSignature,
    invalidatePolicyContext,
    loadCatalog,
    hasRequiredFilters,
    savingPrices,
    savingSettings,
    selectedFormatIds,
  ]);

  const handleConfirmRecalculation = () => {
    const pending = recalculationConfirmation;
    setRecalculationConfirmation(null);
    if (!pending || pending.branchId !== activeBranchIdRef.current
      || pending.scopeVersion !== recalculationScopeVersionRef.current) return;
    void performBulkUpdate({}, {
      formatIds: pending.formatIds,
      recalculate: true,
      scopeVersion: pending.scopeVersion,
    });
  };

  const handleToggleFormatAvailability = useCallback(({
    product,
    variety,
    format,
    isAvailable,
  }) => {
    if (isOfferFormat(format)) return;
    if (availabilityScope?.branchId !== String(branchId) || availabilityScope.loading
      || availabilityScope.error || availabilityScope.isMainBranch) {
      setProductConfirmation({ product, isAvailable, configuredCount: 1, branchId: String(branchId),
        formatIds: [format.id], formatTarget: { product, variety, format }, additionalBranchIds: [] });
      return;
    }
    return performFormatUpdate({ product, variety, format, payload: { isAvailable }, operation: 'availability' });
  }, [branchId, performFormatUpdate, availabilityScope]);

  const handleConfirmProductAvailability = async () => {
    const pending = productConfirmation;
    const requestBranchId = String(branchId || '');
    if (!pending || !canUpdate || !requestBranchId || productAvailabilityOperationRef.current
      || availabilityScope?.branchId !== requestBranchId || availabilityScope.loading || availabilityScope.error) return;
    if (String(pending.branchId) !== requestBranchId
      || activeBranchIdRef.current !== requestBranchId) {
      setProductConfirmation(null);
      return;
    }
    const { product, isAvailable } = pending;
    const formatIds = (pending.formatIds || getCatalogFormatIds([product])).filter((id) => !isOfferFormat(findCatalogFormat(catalogRef.current, id)));
    if (!formatIds.length || formatIds.some(
      (formatId) => !findCatalogFormat(visibleCatalogRef.current, formatId)
    )) {
      setProductConfirmation(null);
      return;
    }
    if (savingProducts[product.id]) return;
    const operation = { branchId: requestBranchId, generation: branchGenerationRef.current };
    productAvailabilityOperationRef.current = operation;
    const isCurrent = () => activeBranchIdRef.current === requestBranchId && branchGenerationRef.current === operation.generation;
    setProductConfirmation({ ...pending, error: '' });
    setSavingProducts((current) => ({ ...current, [product.id]: true }));
    setPageError('');
    try {
      if (pending.formatTarget && !availabilityScope.isMainBranch) {
        const saved = await performFormatUpdate({ ...pending.formatTarget, payload: { isAvailable }, operation: 'availability' });
        if (isCurrent()) setProductConfirmation(saved ? null : { ...pending, error: 'No se pudo actualizar la disponibilidad. Intenta nuevamente.' });
        return;
      }
      const response = availabilityScope.isMainBranch
        ? await updatePriceListAvailabilityBranches(branchId, product.id, { isAvailable, formatIds,
          additionalBranchIds: pending.additionalBranchIds || [] })
        : await updateQuickPriceListProductAvailability(branchId, product.id, { isAvailable, formatIds });
      if (!isCurrent()) return;
      setProductConfirmation(null);
      catalogRequestIdRef.current += 1;
      setRefreshing(false);
      const next = updateProductAvailabilityInCatalog(
        catalogRef.current,
        product.id,
        isAvailable,
        formatIds
      );
      catalogRef.current = next;
      setCatalog(next);
      const updatedCount = Number(response?.updatedCount ?? response?.updated ?? pending.configuredCount);
      const unchangedCount = Number(response?.unchangedCount ?? 0);
      const unconfiguredCount = Number(
        response?.unconfiguredCount ?? response?.skippedCount ?? response?.skipped ?? 0
      );
      setNotice([
        `${Number.isFinite(updatedCount) ? updatedCount : pending.configuredCount} formatos actualizados.`,
        response?.branches?.length > 1 ? `Disponibilidad aplicada en ${response.branches.length} sucursales.` : '',
        Number.isFinite(unchangedCount) && unchangedCount > 0
          ? `${unchangedCount} ya estaban en ese estado.`
          : '',
        Number.isFinite(unconfiguredCount) && unconfiguredCount > 0
          ? `${unconfiguredCount} sin precio omitidos.`
          : '',
      ].filter(Boolean).join(' '));
    } catch (error) {
      if (!isCurrent()) return;
      const message = resolveErrorMessage(error, 'No se pudo actualizar la disponibilidad del producto.');
      setProductConfirmation({ ...pending, error: message });
    } finally {
      if (productAvailabilityOperationRef.current === operation) productAvailabilityOperationRef.current = null;
      if (isCurrent()) {
        setSavingProducts((current) => ({ ...current, [product.id]: false }));
      }
    }
  };

  const filterControls = <QuickPriceListFilters
    branchId={branchId}
    branches={canReadBranches ? promotionBranchOptions : [{ id: branchId, branchName: `Sucursal ${branchId} (actual)` }]}
    branchesLoading={promotionBranchesLoading}
    branchesError={canReadBranches ? promotionBranchesError : ''}
    onBranchChange={(nextBranchId) => {
      if (!catalogLoading && nextBranchId !== String(branchId) && canReadBranches
        && promotionBranches.some((branch) => !branch.isDeleted && String(branch.id) === nextBranchId)) {
        navigate(`/branches/${nextBranchId}/price-list`);
      }
    }}
    query={query} onQueryChange={setQuery}
    formatOptions={formatOptions} formatCode={formatCode} onFormatChange={setFormatCode}
    categoryOptions={categoryOptions} categoryId={categoryId} onCategoryChange={setCategoryId}
  />;

  if (status === 'loading' || status === 'idle') {
    return (
      <main className="min-h-screen bg-slate-100 px-4 pb-10 pt-3 md:pt-4">
        <div className="mx-auto flex max-w-screen-2xl justify-center rounded-2xl bg-white p-10 text-slate-600 shadow-sm">
          <InlineLoading label="Cargando lista de precios" />
        </div>
      </main>
    );
  }

  if (!canRead) {
    return (
      <main className="min-h-screen bg-slate-100 px-4 pb-10 pt-3 md:pt-4">
        <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <h1 className="text-lg font-bold text-amber-950">Sin acceso a precios</h1>
          <p className="mt-2 text-sm text-amber-800">Necesitas el permiso price.read para consultar esta lista.</p>
        </div>
      </main>
    );
  }

  return (
    <main aria-busy={catalogLoading} className="min-h-screen bg-slate-100 px-2 pb-12 pt-3 sm:px-4 md:pt-4 lg:px-6">
      {pendingTarget && pendingTarget.branchId === String(branchId) && <PriceListPendingChangesDialog
        key={`${branchId}:${pendingTarget.format.id}`}
        target={pendingTarget}
        onClose={() => setPendingTarget(null)}
        onResolved={() => {
          if (activeBranchIdRef.current !== pendingTarget.branchId) return;
          setBulkRefresh((value) => value + 1);
          void loadCatalog({ silent: true, event: { priceIds: [pendingTarget.format.id] } });
        }}
        disabled={!canUpdate || savingBulk || anyMutationSaving}
        canUpdateProduct={canUpdateRegularPricing && canUpdateProduct} canUpdatePrice={canUpdateRegularPricing}
        complexPriceRules={complexPriceRules} userId={user?.id}
      />}
      {completionTarget && completionTarget.branchId === String(branchId) && <FormatCompletionDialog
        key={`${branchId}:${completionTarget.format.id}`}
        {...completionTarget}
        canUpdateProduct={canUpdateProduct}
        canUpdatePrice={canUpdateRegularPricing}
        onClose={() => {
          const formatId = completionTarget.format.id;
          setCompletionTarget(null);
          window.setTimeout(() => {
            const row = document.getElementById(`quick-format-${formatId}`);
            if (row && !row.contains(document.activeElement)) {
              row.setAttribute('tabindex', '-1');
              row.focus();
            }
          }, 0);
        }}
        onSaved={async (response) => {
          if (activeBranchIdRef.current !== completionTarget.branchId) return;
          applyFormatResponse(completionTarget.format.id, response, {}, { includeAvailability: true });
          await loadCatalog({ silent: true, event: { priceIds: [completionTarget.format.id] } });
        }}
      />}
      <BranchOffersProvider scopeKey={`${user?.selectedCompanyId}:${branchId}`} overview={branchOverview.data}
        loading={branchOverview.loading} now={promotionNow}
        onNotice={setNotice}
        disabled={anyMutationSaving || savingBulk || catalogLoading}
        onRefresh={async () => {
          await branchOverview.refresh();
          await loadCatalog({ silent: true });
        }}>
      <fieldset
        disabled={catalogLoading || Boolean(productConfirmation)}
        aria-label="Lista de precios"
        className={`mx-auto min-w-0 max-w-screen-2xl space-y-4 border-0 p-0 ${catalogLoading ? '[&_button]:cursor-wait [&_button]:opacity-50 [&_input]:opacity-50 [&_select]:opacity-50' : ''}`}
        onClickCapture={catalogLoading ? (event) => {
          // Fieldsets disable form controls; also block disclosure menus and row actions.
          event.preventDefault();
          event.stopPropagation();
        } : undefined}
        onKeyDownCapture={catalogLoading ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
          }
        } : undefined}
      >
        <QuickPriceListHeader
          ref={stickyToolbarRef}
          loadingLabel={catalogLoading
            ? `Cargando precios de ${promotionBranchOptions.find((branch) => String(branch.id) === String(branchId))?.branchName || `sucursal ${branchId}`}…`
            : ''}
          canUpdate={canUpdateRegularPricing}
          pendingRecalculationCount={pendingRecalculationCount}
          recalculating={recalculating}
          recalculationDisabled={recalculationDisabled}
          recalculationTitle={visibleFormatIds.length > 1000
            ? 'Filtra la lista a un máximo de 1000 formatos para recalcular.'
            : pendingRecalculationCount ? `${pendingRecalculationCount} precios visibles necesitan actualizarse con los costos y reglas guardados de la principal.`
            : 'Los precios visibles están actualizados o no tienen datos suficientes para recalcular.'}
          onProjections={canReadBranches ? () => navigate(`/branches/${branchId}/highlights`) : undefined}
          onHistory={() => navigate(`/branches/${branchId}/price-list/promotions`)}
          onPdf={() => setPdfOpen(true)}
          onRecalculate={() => {
            const formatIds = [...visibleFormatIds];
            const draftError = getRecalculationDraftError(dirtyFormatFieldsRef.current, formatIds);
            if (draftError) {
              setPageError(draftError);
              return;
            }
            setRecalculationConfirmation({
              branchId: String(branchId),
              formatIds,
              scopeVersion: recalculationScopeVersionRef.current,
              categoryLabel: categoryOptions.find((option) => option.id === categoryId)?.label,
              formatLabel: formatOptions.find((option) => option.value === formatCode)?.label,
            });
          }}
        >
          {!loadError && canUpdate ? (
            <QuickPriceBulkEditor
              canEditPricing={canUpdatePricing}
              filters={filterControls}
              selectedCount={selectedFormatIds.size}
              selectedVisibleCount={selectedVisibleCount}
              visibleCount={visibleFormatIds.length}
              lockedVisibleCount={lockedVisibleFormatIds.length}
              selectedLockedCount={selectedLockedFormatIds.length}
              selectedUnlockedCount={selectedUnlockedFormatIds.length}
              allVisibleSelected={allVisibleSelected}
              saving={savingBulk}
              disabled={selectedMutationsSaving || anyPromotionSaving || anyFormatRecalculating
                || anyLockSaving || Boolean(costOverrideReview) || Boolean(stockInputTarget)}
              lockSaving={anyLockSaving}
              selectedPricingBases={selectedPricingBases}
              offerView={isOfferFormat({ productFormatCode: formatCode })}
              selectedFormats={catalog.flatMap((product) => product.varieties.flatMap((variety) => variety.formats))
                .filter((format) => selectedFormatIds.has(String(format.id)))}
              selectedUnconfiguredCount={selectedUnconfiguredCount}
              complexPriceRules={complexPriceRules}
              onSelectVisible={() => handleSetFormatsSelected(selectableVisibleFormatIds, true)}
              onClearSelection={handleClearFormatSelection}
              onLockSelected={() => performPriceLockUpdate({
                formats: selectedUnlockedFormatIds
                  .map((formatId) => findCatalogFormat(catalogRef.current, formatId))
                  .filter(Boolean),
                isLocked: true,
              })}
              onUnlockSelected={() => performPriceLockUpdate({
                formats: selectedLockedFormatIds
                  .map((formatId) => findCatalogFormat(catalogRef.current, formatId))
                  .filter(Boolean),
                isLocked: false,
              })}
              onCreatePromotions={openBulkPromotionDialog}
              onApply={(changes) => performBulkUpdate(changes)}
            />
          ) : <div className="p-3">{filterControls}</div>}
        </QuickPriceListHeader>

        {targetFormatId && (
          <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 sm:flex-row sm:items-center sm:justify-between">
            <span>{hasRequiredFilters
              ? 'Mostrando y destacando el formato solicitado.'
              : 'Selecciona una categoría y un formato para ver el formato solicitado.'}</span>
            <button
              type="button"
              onClick={() => navigate(`/branches/${branchId}/price-list`)}
              className="w-fit rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-100"
            >
              Ver lista completa
            </button>
          </div>
        )}
        {loadError && (
          <div role="alert" className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 sm:flex-row sm:items-center sm:justify-between">
            <span>{loadError}</span>
            <button type="button" onClick={() => void loadCatalog()} className="rounded-lg border border-red-300 px-3 py-1.5 hover:bg-red-100">Reintentar</button>
          </div>
        )}
        {refreshError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{refreshError}</div>}
        {pendingChanges.error && <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {pendingChanges.error} <button type="button" className="underline" onClick={pendingChanges.refresh}>Reintentar advertencias</button>
        </div>}
        {pageError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{pageError}</div>}
        {recalculationResult && (
          <section role="status" className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <p className="font-semibold">
              Recálculo completado: {recalculationResult.updatedCount} precios aplicados,
              {' '}{recalculationResult.unchangedCount} sin cambios y {recalculationResult.skippedCount} omitidos.
            </p>
            {recalculationResult.skippedCount > 0 && (
              <>
                <p className="mt-1 text-xs">
                  Los formatos omitidos conservan su precio. Revisa que tengan una regla válida, costo disponible y peso o conversión cuando corresponda.
                </p>
                {recalculationResult.skipped.length > 0 && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer font-semibold">Ver formatos omitidos</summary>
                    <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                      {recalculationResult.skipped.map((entry) => (
                        <li key={entry.varietyPriceId}>
                          <strong>{entry.label}:</strong> {entry.message || 'No hay una regla o datos suficientes para calcular el precio.'}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}
          </section>
        )}
        <div aria-live="polite" className="sr-only">{notice}</div>
        {notice && (
          <div className="fixed bottom-4 right-4 z-[1150] whitespace-pre-line rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 shadow-lg">
            {notice}
          </div>
        )}



        {!catalogLoading && !loadError && !hasRequiredFilters && (
          <div role="status" className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-600">
            Selecciona una categoría y un formato para ver los productos.
          </div>
        )}
        {!loadError && hasRequiredFilters && (
          <QuickPriceCatalog
            hideOfferSummaries={Boolean(branchOverview.data?.isMainBranch && branchOverview.data?.offers?.length)}
            key={`${catalogBranchId}:${catalogGeneration}`}
            stickyToolbarRef={stickyToolbarRef}
            products={filteredCatalog}
            availabilityProducts={catalog}
            complexPriceRules={complexPriceRules}
            canUpdate={canUpdate}
            canUpdateRegularPricing={canUpdateRegularPricing}
            onCompleteFormat={(canUpdateRegularPricing && (canUpdate || canUpdateProduct)) ? (target) => setCompletionTarget({ ...target, branchId: String(branchId) }) : undefined}
            pendingOperationsByFormat={pendingChanges.operationsByFormat}
            onReviewPendingFormat={(target) => setPendingTarget({ ...target, branchId: String(branchId) })}
            canCorrectStockCosts={canUpdateRegularPricing && canCorrectStockCosts}
            canCreateStock={canCreateStock}
            savingPrices={savingPrices}
            savingAvailability={savingAvailability}
            savingProducts={savingProducts}
            savingSettings={savingSettings}
            savingLocks={savingLocks}
            recalculationReadyFormats={recalculationReadyFormats}
            recalculationBlockedFormats={Object.fromEntries(Object.keys(recalculationReadyFormats).map(id => [id, !actionableRecalculationFormats[id]]))}
            recalculatingFormats={recalculatingFormats}
            promotionNow={promotionNow}
            savingPromotions={savingPromotions}
            promotionErrors={promotionErrors}
            errorsByFormat={errorsByFormat}
            priceResetSignals={priceResetSignals}
            costResetSignals={costResetSignals}
            ruleResetSignals={ruleResetSignals}
            selectedFormatIds={selectedFormatIds}
            mutationDisabled={catalogLoading || savingBulk || Boolean(stockInputTarget)}
            lockAllRows={catalogLoading || recalculating || Boolean(stockInputTarget)}
            targetFormatId={targetFormatId}
            onSavePrice={handleSavePrice}
            onPriceDraftChange={handlePriceDraftChange}
            onSaveCost={handleSaveCost}
            onEditCalculatedCost={handleEditCalculatedCost}
            onAddStockInput={handleAddStockInput}
            onSaveRule={handleSaveRule}
            onCostDirtyChange={(formatId, dirty) => (
              handleFormatFieldDirtyChange(formatId, 'cost', dirty)
            )}
            onRuleDirtyChange={(formatId, dirty) => (
              handleFormatFieldDirtyChange(formatId, 'rule', dirty)
            )}
            onPriceDirtyChange={(formatId, dirty) => (
              handleFormatFieldDirtyChange(formatId, 'price', dirty)
            )}
            onRecalculatePrice={performFormatRecalculation}
            onSetPriceLocks={performPriceLockUpdate}
            onCreatePromotion={openPromotionDialog}
            onEditPromotion={openPromotionDialog}
            onEndPromotion={(pending) => setPromotionConfirmation(pending)}
            onEditOffer={(pending) => openOfferAction(pending)}
            onEndOffer={(pending) => openOfferAction(pending, true)}
            offerBusy={Object.fromEntries(catalog.flatMap((product) => product.varieties.flatMap((variety) => variety.formats.map((format) => [format.id,
              Boolean(savingPromotions[format.id] || savingPrices[format.id] || savingAvailability[format.id] || savingSettings[`${format.id}:rule`]
                || savingLocks[format.id] || recalculatingFormats[format.id])]))))}
            onToggleFormatSelection={handleToggleFormatSelection}
            onSetFormatsSelected={handleSetFormatsSelected}
            onToggleFormatAvailability={handleToggleFormatAvailability}
            onRequestProductAvailability={(pending) => setProductConfirmation({ ...pending, branchId: String(branchId), additionalBranchIds: [] })}
          />
        )}
      </fieldset>
      </BranchOffersProvider>

      {stockInputTarget && stockInputTarget.branchId === String(branchId) && (
        <AddStockInputDialog
          key={`${stockInputTarget.branchId}:${stockInputTarget.variety.id}`}
          {...stockInputTarget}
          canCreateStock={canCreateStock}
          canReadProviders={canReadProviders}
          isCurrent={() => activeBranchIdRef.current === stockInputTarget.branchId
            && branchGenerationRef.current === stockInputTarget.generation}
          onSaved={handleStockInputSaved}
          onClose={() => setStockInputTarget(null)}
        />
      )}

      <CostOverrideDialog
        open={Boolean(costOverrideReview)}
        productLabel={costOverrideReview?.product?.name}
        varietyLabel={costOverrideReview?.variety?.name}
        formatLabel={costOverrideReview?.format?.productFormatName}
        manualBaseCost={costOverrideReview?.manualBaseCost}
        basis={costOverrideReview?.product?.pricingBasis}
        costs={costOverrideReview?.format?.costs}
        onPreview={handlePreviewCostOverride}
        onApply={handleApplyCostOverride}
        onCancel={handleCancelCostOverride}
      />

      <ConfirmModal
        isOpen={Boolean(recalculationConfirmation)}
        title="¿Recalcular precios?"
        message={recalculationConfirmation
          ? `Se revisarán ${recalculationConfirmation.formatIds.length} formatos visibles de ${recalculationConfirmation.categoryLabel} · ${recalculationConfirmation.formatLabel}${query.trim() ? ` (búsqueda: ${query.trim()})` : ''}. Se aplicarán los precios calculados con las reglas y costos guardados más recientes. Los formatos sin regla o sin datos suficientes conservarán su precio. No se modificarán reglas, costos ni disponibilidad. Los precios regulares se actualizarán por igual en todas las sucursales.`
          : ''}
        confirmLabel="Recalcular y aplicar"
        confirmDisabled={recalculationDisabled}
        onCancel={() => setRecalculationConfirmation(null)}
        onConfirm={handleConfirmRecalculation}
      />

      <ProductAvailabilityDialog target={productConfirmation} scope={availabilityScope}
        saving={Boolean(productConfirmation && savingProducts[productConfirmation.product.id])}
        onChangeBranches={(additionalBranchIds) => setProductConfirmation((current) => ({ ...current, additionalBranchIds }))}
        onRetry={() => setAvailabilityScopeRetry((value) => value + 1)}
        onClose={() => setProductConfirmation(null)}
        onConfirm={() => void handleConfirmProductAvailability()}
      />

      {offerAction && !offerAction.end && <EditPriceListOfferDialog
        key={`${offerAction.branchId}:${offerAction.format.id}`} target={offerAction}
        saving={Boolean(savingPromotions[offerAction.format.id])} error={offerActionError}
        onClose={() => setOfferAction(null)} onSubmit={performOfferAction} />}
      <ConfirmModal isOpen={Boolean(offerAction?.end)} title="¿Quitar Oferta?"
        message={offerAction ? `La Oferta de ${offerAction.variety.name} dejará de estar a la venta en esta sucursal. Detalle conservará su disponibilidad actual.` : ''}
        confirmLabel={savingPromotions[offerAction?.format.id] ? 'Quitando…' : 'Quitar Oferta'} tone="warning"
        confirmDisabled={Boolean(savingPromotions[offerAction?.format.id])}
        onCancel={() => { if (!savingPromotions[offerAction?.format.id]) setOfferAction(null); }} onConfirm={() => performOfferAction()}>
        {offerActionError && <p role="alert" className="mt-3 text-sm text-red-700">{offerActionError}</p>}
      </ConfirmModal>

      <PromotionDateRangeDialog
        isOpen={Boolean(promotionDialog)}
        branchId={branchId}
        branches={promotionBranchOptions}
        branchesLoading={promotionBranchesLoading}
        branchesError={promotionBranchesError}
        targets={promotionDialog?.targets || []}
        promotion={promotionDialog?.promotion || null}
        saving={(promotionDialog?.targets || []).some((target) => (
          Boolean(savingPromotions[String(target.id)])
        ))}
        error={promotionDialogError}
        onClose={() => {
          setPromotionDialog(null);
          setPromotionDialogError('');
        }}
        onSubmit={(payload) => performPromotionSave(promotionDialog, payload)}
      />

      <ConfirmModal
        isOpen={Boolean(promotionConfirmation)}
        title={getPromotionState(promotionConfirmation?.promotion, promotionNow) === 'scheduled'
          ? '¿Cancelar precio temporal programado?'
          : '¿Finalizar precio temporal activo?'}
        message={promotionConfirmation?.promotion
          ? `${formatPromotionTerms(promotionConfirmation.promotion)} dejará de mostrarse en la lista de precios. La versión seguirá disponible en el historial.`
          : ''}
        confirmLabel={getPromotionState(promotionConfirmation?.promotion, promotionNow) === 'scheduled'
          ? 'Cancelar precio temporal'
          : 'Finalizar precio temporal'}
        confirmDisabled={Boolean(savingPromotions[String(
          promotionConfirmation?.promotion?.varietyPriceId
            || promotionConfirmation?.format?.id
            || ''
        )])}
        tone="warning"
        onCancel={() => setPromotionConfirmation(null)}
        onConfirm={() => void performPromotionEnd(promotionConfirmation)}
      />

      <QuickPriceListPdfDialog
        isOpen={pdfOpen}
        branchId={branchId}
        catalog={catalog}
        formatOptions={formatOptions}
        categoryOptions={categoryOptions}
        initialQuery={query}
        initialFormatCode={formatCode}
        initialCategoryId={categoryId}
        onClose={() => setPdfOpen(false)}
      />
    </main>
  );
};

export default QuickPriceListPage;
